import {
	Inline,
	Alert,
	Heading,
	Badge,
	Box,
	Button,
	Icons,
	Table,
	Tooltip,
	TableDataCell,
	Stack,
	Text,
	Col,
	Component,
	Container,
	Divider,
	Grid,
	joinAttributes,
	RelativeTime,
	Spinner,
	Stars,
	TabContent,
	Tabs,
	TabsList,
	Time,
	TimeDuration,
	MoneySum,
	BoxOwnProps,
	Popover,
	CheckboxInput,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { NavLink, queryToSearch, ButtonLink } from "@sembark-travel/ui-router"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	utcTimestampToLocalDate,
	duration as createDuration,
	getDiff,
	localOrUtcTimestampToLocalDate,
	getDiffBetweenLocalAndUtcTimestamp,
	getTimezoneOffset,
	formatDate,
	parseDate,
	addUnit,
} from "@sembark-travel/datetime-utils"
import { useXHR, xhr } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import React, { useMemo, useRef } from "react"
import useSWR from "swr"
import { $PropertyType } from "utility-types"
import { generatePath, useNavigate } from "../router-utils"
import { collect } from "../utils"
import { GeneratedItineraryAsset } from "./GenerateItineraryAssets"
import {
	GivenPackagePriceDetailsInDialog,
	GivenQuotePaymentLink,
	GiveQuote,
	GivenPricesPerPersonSharing,
} from "./GiveQuote"
import ItineraryItem from "./ItineraryItem"
import { ShareQuoteInDialog } from "./ShareQuote"
import {
	IQuote,
	IQuoteDiff,
	ITrip,
	ITripQuote,
	useFetchQuote,
	getBasicInfoForQueryFromTrip,
	quotesXHR as XHR,
	useDiffBetweenLatestQuoteAndBookings,
	filterHotelNotes,
	transformFlightsDetailsToPreview,
} from "./store"
import {
	getDaywiseTransportsAndActivitiesForQuote,
	getGivenQuoteTaxInfo,
	getTripQuoteDetailsDiff,
	shouldAllowQuoteActions,
	tempDisableQuoteActionsMessageAlert,
} from "./utils"
import {
	numberToLocalString,
	withOrdinalSuffix,
} from "@sembark-travel/number-utils"
import { PERMISSIONS, useCheckPermissions, useHasFeatureFlag } from "../Auth"
import { Money } from "@sembark-travel/ui-base"
import {
	TMoney,
	addMoneyFromDifferentCurrencies,
	divideMoneyBy,
	formatMoneyByIntl,
	moneyParseByDecimal,
	multipleMoneyBy,
	roundMoney,
} from "@sembark-travel/money"
import { getPriceBirfucationAsText } from "../HotelPrices/utils"
import { isTruthy } from "@sembark-travel/ui-form"
import {
	TBrandWiseColors,
	TTripQuotePdfTemplate,
} from "../EmailPdfTemplates/store"
import { QUOTE_SPECIAL_PDF_SETTINGS } from "./store"
import { isTicketTypeClosedOnDate } from "../TravelActivities/utils"
import { usePaymentGatewayIntegratedAppsCount } from "../Integrations"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	PreviewFlightOnDialog,
	IPreviewFlightData,
} from "../Flights/NewCalculatePrice"
import { IFlightPricing } from "../Flights"
import { CabCapacityWarningMessage, ShowCabCapacityInfo } from "../CabTypes"
import { formatDuration } from "../TransportServicePrices"
import { getRoomStatusOnDate } from "../HotelBookings"
import { StepIcon } from "./NewQuote"
import {
	getEntryTypeDisplayName,
	visaProcessingTimeStr,
	getPurposeOfVisaDisplayName,
} from "../Visa"
import { VisaTypeDisabled } from "../VisaBookings"
import { isVisaRateExpired } from "../VisaServicePrices"
import { TTripDestination } from "../TripDestinations"
import { EntryTypeIcon } from "../shared/EntryTypeConfig"

export default function Quotes({
	trip,
	children,
}: {
	trip: ITrip
	children?: (props: { defaultQuoteId: number }) => React.ReactNode
}) {
	const xhr = useXHR()
	const { data: tripQuotes } = useSWR<ITripQuote[]>(
		`trip/${trip.id}/quotes`,
		() => XHR(xhr).getQuotes(trip.id)
	)
	const { latest_given_quote } = trip
	const [convertedQuote, quoteLatestGiven, otherQuotes, afterConversionQuotes] =
		useMemo(
			() =>
				(tripQuotes || []).reduce<
					[
						ITripQuote | undefined,
						ITripQuote | undefined,
						ITripQuote[],
						ITripQuote[],
					]
				>(
					(
						[
							convertedQuote,
							quoteLatestGiven,
							otherQuotes,
							afterConversionQuotes,
						],
						quote
					) => {
						let pushItIntoTheList = true
						if (quote.given_quote?.used_for_conversion) {
							pushItIntoTheList = false
							convertedQuote = quote
						} else if (
							quote.given_quote &&
							latest_given_quote &&
							quote.given_quote.id === latest_given_quote.id
						) {
							pushItIntoTheList = false
							quoteLatestGiven = quote
						}
						if (quote.created_after_conversion) {
							pushItIntoTheList = false
							afterConversionQuotes.push(quote)
						}
						if (pushItIntoTheList) {
							otherQuotes.push(quote)
						}

						return [
							convertedQuote,
							quoteLatestGiven,
							otherQuotes,
							afterConversionQuotes,
						]
					},
					[undefined, undefined, [], []]
				),
			[tripQuotes, latest_given_quote]
		)
	return (
		<Box marginY="4">
			{!tripQuotes ? (
				<Spinner padding="4" alignCenter />
			) : !tripQuotes.length ? null : (
				<Tabs>
					<TabsList style={{ minWidth: "150px" }}>
						{afterConversionQuotes.length
							? afterConversionQuotes.map((quote) => (
									<QuoteLink
										quote={quote}
										key={quote.id}
										latestGiven={quote.id === quoteLatestGiven?.id}
									/>
								))
							: null}
						{convertedQuote ? (
							<QuoteLink
								quote={convertedQuote}
								key={convertedQuote.id}
								converted
								latestGiven={
									convertedQuote.given_quote &&
									latest_given_quote &&
									convertedQuote.given_quote.id === latest_given_quote.id
								}
							/>
						) : null}
						{afterConversionQuotes.length === 0 && quoteLatestGiven ? (
							<QuoteLink
								quote={quoteLatestGiven}
								key={quoteLatestGiven.id}
								latestGiven
							/>
						) : null}
						{otherQuotes.map((quote) => (
							<QuoteLink quote={quote} key={quote.id} />
						))}
					</TabsList>
					<TabContent
						bgColor="default"
						roundedRight="lg"
						overflow="auto"
						style={{ minHeight: "400px" }}
					>
						{children
							? children({
									defaultQuoteId: (afterConversionQuotes?.length &&
									quoteLatestGiven
										? quoteLatestGiven
										: convertedQuote ||
											quoteLatestGiven ||
											afterConversionQuotes[0] ||
											tripQuotes[0]
									).id,
								})
							: null}
					</TabContent>
				</Tabs>
			)}
		</Box>
	)
}

function QuoteLink({
	quote: tripQuote,
	latestGiven,
	converted,
}: {
	quote: ITripQuote
	latestGiven?: boolean
	converted?: boolean
}) {
	const {
		given_quote,
		quote,
		shared_by_tenant,
		shared_with_tenant,
		created_after_conversion,
		used_for_conversion,
		used_for_holding,
		ai_autofilled,
	} = tripQuote
	const allOptions = [tripQuote].concat(tripQuote.options || [])
	return (
		<NavLink
			key={tripQuote.id}
			to={generatePath("/trips/:tripId/quotes/:quoteId", {
				tripId: String(tripQuote.trip_id),
				quoteId: String(tripQuote.id),
			})}
			replace
			style={{
				minWidth: "150px",
			}}
			position="relative"
		>
			<Box
				position="absolute"
				top="0"
				right="0"
				paddingTop="1"
				paddingRight="2"
			>
				{used_for_conversion || used_for_holding ? (
					<Tooltip
						content={
							latestGiven
								? `Latest Quote used for ${
										used_for_conversion ? "Conversion" : "Holding"
									}`
								: `Used for ${used_for_conversion ? "Conversion" : "Holding"}`
						}
					>
						<Box>
							{used_for_conversion ? (
								<Icons.OkCircleSolid color="success" />
							) : (
								<Icons.FlagSolid color="warning" />
							)}
						</Box>
					</Tooltip>
				) : created_after_conversion ? (
					latestGiven ? (
						<Tooltip content={"Latest Quote after conversion"}>
							<Box>
								<Icons.OkCircleSolid color="primary" />
							</Box>
						</Tooltip>
					) : (
						<Tooltip content={"After Conversion Quote"}>
							<Box padding="1" display="inlineBlock">
								<Box
									display="inlineBlock"
									width="3"
									height="3"
									rounded="full"
									borderWidth="1"
									borderColor="primary_emphasis"
									bgColor="primary"
								></Box>
							</Box>
						</Tooltip>
					)
				) : latestGiven ? (
					<Tooltip content="Latest Quote">
						<Box padding="1" display="inlineBlock">
							<Box
								display="inlineBlock"
								width="3"
								height="3"
								rounded="full"
								borderWidth="1"
								borderColor="primary_emphasis"
								bgColor="primary"
							></Box>
						</Box>
					</Tooltip>
				) : null}
			</Box>
			<Box>
				{allOptions.length > 1 ? (
					<Text fontSize="sm">
						{pluralize("Option", allOptions.length, true)}
					</Text>
				) : null}
				<Box>
					{allOptions.map(({ given_quote, id }, index) =>
						given_quote ? (
							<Box
								key={id}
								fontSize={
									index > 0 ? "lg" : converted || latestGiven ? "3xl" : "xl"
								}
								fontWeight="semibold"
								title="Package Price"
								color={
									converted ? "success" : latestGiven ? "primary" : "muted"
								}
							>
								{index > 0 ? <Text as="span">/ </Text> : null}
								<Money
									currency={given_quote.given_currency}
									amount={given_quote.given_price}
								/>
							</Box>
						) : null
					)}
				</Box>
				{!given_quote ? (
					<Box color="warning" fontWeight="semibold">
						<Icons.Attention /> Price Not Set
					</Box>
				) : null}
			</Box>
			<Box fontSize="sm">
				{joinAttributes(
					<Time
						value={localOrUtcTimestampToLocalDate(
							quote.start_date_local,
							quote.start_date
						)}
						format="D MMM"
					/>,
					<TimeDuration value={createDuration(quote.days, "days")}>
						{quote.days}D
					</TimeDuration>,
					<Box
						display="inline"
						title={`${quote.no_of_adults} Adults and ${quote.no_of_children} Children`}
					>
						{quote.no_of_adults}A
						{quote.no_of_children > 0 ? `,${quote.no_of_children}C` : ""}
					</Box>
				)}
			</Box>
			<RelativeTime
				value={utcTimestampToLocalDate((given_quote || quote).created_at)}
				color="muted"
				fontSize="xs"
				display="block"
			/>
			{shared_by_tenant ? (
				<Box marginTop="2">
					<Badge title={`Received from ${shared_by_tenant.name}`}>
						<Icons.ChevronDown rotate="270" /> {shared_by_tenant.short_name}
					</Badge>
				</Box>
			) : null}
			{shared_with_tenant ? (
				<Box marginTop="2">
					<Badge title={`Sent to ${shared_with_tenant.name}`}>
						<Icons.ChevronDown rotate="90" /> {shared_with_tenant.short_name}
					</Badge>
				</Box>
			) : null}
			{ai_autofilled ? (
				<Text fontSize={"xs"} color="accent">
					AI Parsed <Icons.SparklesSolid width={"3"} height={"3"} />
				</Text>
			) : null}
		</NavLink>
	)
}

export function QuoteRoute({
	quoteId,
	readOnly,
	canBeConverted,
	canSendToHolding,
	onChange,
}: {
	quoteId: number | string
	readOnly?: boolean
	canBeConverted: boolean
	canSendToHolding: boolean
	onChange: () => void
}) {
	return (
		<Container fluid paddingY="4" key={quoteId}>
			<Quote
				quoteId={quoteId}
				readOnly={readOnly}
				canBeConverted={canBeConverted}
				canSendToHolding={canSendToHolding}
				onChange={onChange}
			/>
		</Container>
	)
}

interface IQuoteProps {
	quote?: ITripQuote
	quoteId: number | string
	readOnly?: boolean
	canBeConverted?: boolean
	canSendToHolding?: boolean
	hideItineary?: boolean
	onChange?: () => void
	hideOptions?: boolean
}

export function Quote({
	readOnly,
	quoteId,
	quote: initialQuote,
	canBeConverted,
	canSendToHolding,
	hideItineary,
	onChange,
	hideOptions,
}: IQuoteProps) {
	const xhr = useXHR()
	const {
		data: tripQuote,
		mutate,
		isValidating,
	} = useFetchQuote(quoteId, initialQuote, {}, true)
	const navigate = useNavigate()
	async function revalidate() {
		return mutate().then((resp) => {
			onChange?.()
			return resp
		})
	}
	const pdfSettingsPopoverRef = useRef<HTMLDivElement>(null)
	const hasTripHoldingFeature = useHasFeatureFlag("hold_trips")
	const canSharePDF = useHasFeatureFlag("share_quote_pdf")
	const canShareWordDoc = useHasFeatureFlag("share_quote_word_doc")
	const { hasPermission } = useCheckPermissions()
	canSendToHolding = canSendToHolding && hasTripHoldingFeature
	const canEditQuote = true

	const canViewTotalCosting =
		hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ||
		hasPermission(PERMISSIONS.CREATE_QUOTE)

	const canViewAccommocationCosting =
		hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ||
		(hasPermission(PERMISSIONS.CREATE_QUOTE) &&
			hasPermission(PERMISSIONS.MODIFY_HOTEL_BOOKINGS)) ||
		(hasPermission(PERMISSIONS.CREATE_QUOTE) &&
			hasPermission(PERMISSIONS.VIEW_HOTELS))

	const canViewTranportationActivityCosting =
		hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ||
		(hasPermission(PERMISSIONS.CREATE_QUOTE) &&
			hasPermission(PERMISSIONS.MODIFY_CAB_SCHEDULES)) ||
		(hasPermission(PERMISSIONS.CREATE_QUOTE) &&
			hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICES))

	const diffFromTrip = useMemo(() => {
		if (!tripQuote?.trip) return null
		return getTripQuoteDetailsDiff(tripQuote.trip, tripQuote.quote)
	}, [tripQuote])
	const pgIntegratedAppsCount = usePaymentGatewayIntegratedAppsCount()

	if (!tripQuote || !tripQuote.quote) {
		return <Spinner alignCenter padding="4" />
	}
	const {
		id,
		quote,
		trip_id,
		itinerary,
		shared_with_tenant,
		shared_by_tenant,
		created_for_quote_request,
		converted_shared_quote,
		is_latest,
		options,
		given_quote,
		pricing_strategy,
	} = tripQuote
	const {
		start_date,
		start_date_local,
		days,
		cabs = [],
		used_cab_types,
		cabs_price_calculations,
		travel_activities = [],
		transport_extras = [],
		other_extras = [],
		flights,
		visas,
		comments,
		created_by,
		created_at,
		cabs_tax_percentage,
		children,
		no_of_adults,
	} = quote
	readOnly =
		readOnly ||
		!canEditQuote ||
		((
			hasTripHoldingFeature
				? canBeConverted && canSendToHolding
				: canBeConverted
		)
			? false
			: !is_latest)
	const allOptions = [tripQuote].concat(hideOptions ? [] : options || [])

	const timezoneDiff = getDiffBetweenLocalAndUtcTimestamp(
		start_date_local,
		start_date
	)

	const shouldAllowEditQuote = shouldAllowQuoteActions(pricing_strategy) //temp condition

	const processingPaymentLinkTransactionForOption = allOptions.find(
		(o) => o.given_quote?.pg_invoice?.unverified_successful_transactions_count
	)

	const usedOrCreatedAfterHoldingOrConversionOption = allOptions.find(
		(o) =>
			o.used_for_conversion ||
			o.used_for_holding ||
			o.created_after_conversion ||
			o.created_after_holding
	)
	const can_create_quote = hasPermission(PERMISSIONS.CREATE_QUOTE)
	return (
		<Box cursor={isValidating ? "wait" : undefined}>
			<Stack gap="4">
				{timezoneDiff ? (
					<Alert status="warning">
						The Timezone of this quote differs from your local timezone by{" "}
						{timezoneDiff} mins. Please avoid modifying details.
					</Alert>
				) : null}
				{tripQuote?.is_latest &&
				!readOnly &&
				(tripQuote.used_for_holding ||
					tripQuote.created_after_holding ||
					tripQuote.created_after_conversion ||
					tripQuote.used_for_conversion) ? (
					<LatestQuoteDiffFromBookingsForTrip tripId={tripQuote.trip_id} />
				) : null}
				<Inline
					justifyContent="between"
					gap="4"
					collapseBelow="md"
					flexWrap="wrap"
				>
					<Stack as="header" gap="4" maxWidth="full">
						<Inline gap="4" alignItems="center">
							<Heading fontSize="base" as="h5" color="muted">
								Package Quote Price
							</Heading>
							{!readOnly &&
							!shared_with_tenant &&
							!given_quote?.used_for_conversion &&
							!given_quote?.used_for_holding &&
							!processingPaymentLinkTransactionForOption &&
							can_create_quote ? (
								<Component initialState={false}>
									{({ state, setState }) => (
										<>
											<Button
												onClick={() => setState(true)}
												size="sm"
												level={!given_quote ? "primary" : "tertiary"}
												status={!given_quote ? "danger" : undefined}
											>
												<Icons.Pencil />
											</Button>
											<Dialog
												open={state}
												onClose={() => setState(false)}
												title="Edit Selling Price"
												xl
												fitContainer={
													tripQuote.pricing_strategy === "per_component_v2" ||
													tripQuote.pricing_strategy ===
														"per_component_per_person_v2"
														? true
														: false
												}
											>
												<Dialog.Body>
													<GiveQuote
														tripQuote={tripQuote}
														onChange={() => {
															setState(false)
															revalidate()
															setTimeout(() => {
																navigate(`/trips/:tripId`, {
																	params: { tripId: String(trip_id) },
																	replace: true,
																})
															}, 1000)
														}}
														onCancel={() => setState(false)}
													/>
												</Dialog.Body>
											</Dialog>
										</>
									)}
								</Component>
							) : null}
						</Inline>
						<Inline gap="6" flexWrap="wrap">
							{allOptions.map((tripQuote, index, tripQuotes) => {
								const { given_quote, quote } = tripQuote
								const { no_of_adults, no_of_children, complimentary_count } =
									quote
								const chargeAblePax =
									no_of_adults + no_of_children - complimentary_count
								const perPersonPrice = roundMoney(
									divideMoneyBy(
										moneyParseByDecimal(
											given_quote?.given_price ?? 0,
											given_quote?.given_currency || "INR"
										),
										chargeAblePax,
										"ceil"
									),
									"ceil"
								)
								const perPersonPriceFormatted = formatMoneyByIntl(
									perPersonPrice,
									{
										showSolidus: true,
									}
								)
								const perPersonPriceStirng = `${perPersonPriceFormatted} Per Person X ${chargeAblePax} Pax + ${complimentary_count} FOC`

								return (
									<Box
										maxWidth="full"
										key={tripQuote.id}
										position="relative"
										{...(given_quote?.used_for_holding ||
										given_quote?.used_for_conversion
											? {
													paddingX: "4",
													paddingTop: "8",
													paddingBottom: "4",
													borderWidth: "2",
													rounded: "md",
													borderColor: given_quote?.used_for_conversion
														? "success_emphasis"
														: "warning_emphasis",
												}
											: {})}
									>
										{given_quote?.used_for_conversion ||
										given_quote?.used_for_holding ? (
											<Box
												position="absolute"
												left="0"
												right="0"
												width="full"
												top="0"
												paddingY="1"
												paddingX="4"
												bgColor={
													given_quote?.used_for_conversion
														? "success"
														: "warning"
												}
												fontSize="sm"
												fontWeight="semibold"
											>
												{given_quote?.used_for_conversion
													? "Used for Conversion"
													: "Used for Holding"}
											</Box>
										) : null}
										<Stack gap="2">
											{tripQuotes.length > 1 ? (
												<Box>
													<Box
														display="inlineBlock"
														borderBottomWidth="2"
														borderColor="emphasis"
													>
														<Text fontWeight="semibold">
															Option {index + 1}: {tripQuote.name}
														</Text>
													</Box>
												</Box>
											) : null}
											<Inline gap="4">
												<Inline gap="3" alignItems="baseline" flexWrap="wrap">
													<Box>
														{given_quote ? (
															<GivenPackagePriceDetailsInDialog
																tripQuotes={tripQuotes}
																onChange={revalidate}
															>
																{({ show }) => (
																	<Button
																		onClick={
																			canViewTotalCosting ? show : undefined
																		}
																		disabled={!canViewTotalCosting}
																		inline
																	>
																		<Money
																			currency={given_quote.given_currency}
																			amount={given_quote.given_price}
																			color="accent"
																			showCurrency
																			fontSize="xl"
																			fontWeight="semibold"
																		/>
																		<Box
																			as="span"
																			marginLeft="1"
																			color="default"
																		>
																			(
																			{!given_quote.tax_type.display_name
																				? `Tax: `
																				: (() => {
																						const {
																							taxPercentage,
																							shouldDisplay,
																							taxIncluded,
																						} =
																							getGivenQuoteTaxInfo(given_quote)

																						if (taxIncluded) {
																							return shouldDisplay
																								? `inc.${taxPercentage}%`
																								: "inc."
																						} else {
																							return "exc."
																						}
																					})()}{" "}
																			{given_quote.tax_name})
																		</Box>
																	</Button>
																)}
															</GivenPackagePriceDetailsInDialog>
														) : (
															<Text
																color="warning"
																fontSize="xl"
																fontWeight="semibold"
															>
																Not Set
															</Text>
														)}
													</Box>
													{canViewTotalCosting ? (
														<Inline gap="3" alignItems="center">
															<Text fontSize="xl" color="muted">
																/
															</Text>
															<Inline alignItems="baseline" gap="1">
																<Money
																	currency={tripQuote.currency}
																	amount={tripQuote.cost_price}
																	showCurrency
																	fontSize="md"
																/>
																<Text fontSize="sm" color="muted">
																	(cost price)
																</Text>
															</Inline>
														</Inline>
													) : null}
												</Inline>
											</Inline>
											{complimentary_count > 0 ? (
												<Text>{perPersonPriceStirng}</Text>
											) : null}
											{given_quote &&
											given_quote.given_prices_per_person?.length ? (
												<GivenPricesPerPersonSharing
													given_prices_per_person={
														given_quote.given_prices_per_person
													}
												/>
											) : null}
											{given_quote &&
											!readOnly &&
											pgIntegratedAppsCount &&
											!usedOrCreatedAfterHoldingOrConversionOption &&
											(processingPaymentLinkTransactionForOption
												? processingPaymentLinkTransactionForOption.id ===
													tripQuote.id
												: true) ? (
												<Inline gap="2" flexWrap={"wrap"} alignItems="center">
													<Text color="accent" fontWeight="semibold">
														Payment Link:
													</Text>
													<Box>
														<GivenQuotePaymentLink
															givenQuote={given_quote}
															onChange={mutate}
														/>
													</Box>
												</Inline>
											) : null}
											{!readOnly &&
											given_quote &&
											pgIntegratedAppsCount &&
											!given_quote.pg_invoice?.paid_at &&
											given_quote.pg_invoice?.failed_transactions_count ? (
												<Text>
													<Badge status="warning">
														{given_quote.pg_invoice.failed_transactions_count}{" "}
														failed{" "}
														{pluralize(
															"attempt",
															given_quote.pg_invoice.failed_transactions_count
														)}{" "}
														of Payment
													</Badge>
												</Text>
											) : null}
											{given_quote?.comments ? (
												<Box
													as="blockquote"
													style={{
														fontStyle: "italic",
													}}
												>
													Internal Comments: {given_quote.comments}
												</Box>
											) : null}
											{given_quote?.customer_remarks ? (
												<Stack
													borderLeftWidth="2"
													borderColor="success"
													paddingLeft="4"
													gap="2"
													color="success"
												>
													Customer/Agent Remarks:{" "}
													<Markdown>{given_quote.customer_remarks}</Markdown>
												</Stack>
											) : null}
											{index === 0 && comments ? (
												<blockquote>{comments}</blockquote>
											) : null}
										</Stack>
									</Box>
								)
							})}
						</Inline>
						<Stack gap="2">
							<Box fontSize="sm" color="muted">
								{joinAttributes(
									given_quote &&
										getDiff(
											utcTimestampToLocalDate(given_quote.created_at),
											utcTimestampToLocalDate(created_at),
											"minutes"
										) > 1 ? (
										<>
											Rates updated{" "}
											<RelativeTime
												value={utcTimestampToLocalDate(given_quote.created_at)}
											/>{" "}
											by {given_quote.created_by.name}
										</>
									) : null,
									<>
										Created{" "}
										<RelativeTime value={utcTimestampToLocalDate(created_at)} />{" "}
										by {created_by.name}
									</>
								)}
							</Box>
							<Inline gap="2" flexWrap="wrap">
								{tripQuote.created_after_conversion ? (
									<Box>
										<Badge primary>After Conversion Quote</Badge>
									</Box>
								) : !tripQuote.used_for_conversion &&
								  tripQuote.created_after_holding ? (
									<Box>
										<Badge warning>After Holding Quote</Badge>
									</Box>
								) : null}
								{shared_by_tenant ? (
									<Box>
										<Badge success>
											Received from{" "}
											{shared_by_tenant.short_name || shared_by_tenant.name}
										</Badge>
									</Box>
								) : null}
							</Inline>
							{given_quote ? (
								<Stack gap="2">
									{is_latest ? (
										<Box>
											<Badge>Latest Quote</Badge>
										</Box>
									) : null}
									{shared_with_tenant ? (
										<Box>
											<Badge success>
												Sent to{" "}
												{shared_with_tenant.short_name ||
													shared_with_tenant.name}
											</Badge>
										</Box>
									) : created_for_quote_request && given_quote ? (
										<Box>
											<Button
												level="primary"
												onClick={async () => {
													if (
														window.confirm(
															`Are you sure you want to share it with ${created_for_quote_request.from.short_name}. You will NOT be able to edit this quote, selling price and itinerary after sharing. But you can always create a new quote with changes and share.`
														)
													) {
														try {
															await xhr.post("/share-trip-quotes", {
																trip_quote_id: id,
																comments: "Custom comments",
															})
														} catch (e) {
															const error = e as Error
															window.alert(error.message)
														}
														revalidate()
													}
												}}
											>
												Send to {created_for_quote_request.from.short_name}
											</Button>
										</Box>
									) : null}
									{!readOnly &&
									canBeConverted &&
									(!created_for_quote_request || converted_shared_quote) &&
									!processingPaymentLinkTransactionForOption ? (
										<Box>
											<ButtonLink
												to={
													generatePath("/trips/:tripId/convert", {
														tripId: trip_id.toString(),
													}) + queryToSearch({ using: id })
												}
												data-testid="convert_quote_btn"
												level="secondary"
												status="primary"
												size="sm"
											>
												{
													/* Trip Holding is disabled for B2B on Sembark Queries */ shared_by_tenant ||
													!canSendToHolding ||
													created_for_quote_request
														? "Convert using Quote"
														: "Convert/On-Hold using Quote"
												}
											</ButtonLink>
										</Box>
									) : null}
								</Stack>
							) : null}
						</Stack>
					</Stack>
					<Box textAlign="right" paddingY="2" marginLeft="auto">
						<Inline gap="2" justifyContent="end" alignItems="center">
							{!readOnly &&
							!tripQuote.readonly &&
							!processingPaymentLinkTransactionForOption &&
							can_create_quote ? (
								<Box>
									{shouldAllowEditQuote ? (
										<ButtonLink
											level="primary"
											to={
												generatePath("/trips/:tripId/new-quote", {
													tripId: String(tripQuote.trip_id),
												}) +
												`${queryToSearch({
													...getBasicInfoForQueryFromTrip(
														tripQuote.quote,
														tripQuote.id
													),
													editing: 1,
													afterConfirmedQuote:
														tripQuote.used_for_holding ||
														tripQuote.used_for_conversion ||
														tripQuote.created_after_holding ||
														tripQuote.created_after_conversion
															? 1
															: 0,
												})}`
											}
										>
											Edit Quote
										</ButtonLink>
									) : (
										<Button
											level="primary"
											onClick={tempDisableQuoteActionsMessageAlert}
										>
											Edit Quote
										</Button>
									)}
								</Box>
							) : null}
							<ShareQuoteInDialog
								quote={tripQuote}
								isValidating={isValidating}
								isGenerating={itinerary?.is_generating}
								id={itinerary?.id}
								refetchQuote={() => revalidate()}
							/>
							{itinerary &&
							(canShareWordDoc || canSharePDF) &&
							((itinerary.created_from_quote_itinerary &&
								shared_by_tenant &&
								itinerary.created_from_quote_itinerary.file_url) ||
								tripQuote.given_quote) ? (
								<Popover
									trigger={"click"}
									placement="bottom-end"
									interactive
									additionalRootCloseRefs={[pdfSettingsPopoverRef]}
									content={({ hide: hideMenu }) => {
										return (
											<Stack width={"full"} gap="1">
												{itinerary && tripQuote.given_quote && canSharePDF ? (
													<GeneratedItineraryAsset
														id={itinerary.id}
														isGenerating={itinerary.is_generating}
														fileUrl={itinerary.file_url}
														onChange={() => revalidate()}
														level="tertiary"
														downloadLabel={
															<>
																<Icons.DocumentDownload /> Download PDF
															</>
														}
														align="left"
													/>
												) : null}

												{itinerary.created_from_quote_itinerary &&
												shared_by_tenant &&
												itinerary.created_from_quote_itinerary.file_url &&
												canSharePDF ? (
													<GeneratedItineraryAsset
														id={itinerary.created_from_quote_itinerary.id}
														isGenerating={false}
														fileUrl={
															itinerary.created_from_quote_itinerary.file_url
														}
														onChange={() => revalidate()}
														title={`Download quote shared by ${shared_by_tenant.short_name}`}
														downloadLabel={
															<>
																<Icons.DocumentDownload /> Download Received
																Quote
															</>
														}
													/>
												) : null}

												{tripQuote.given_quote ? (
													<>
														{itinerary.word_doc_file_url &&
														!itinerary.is_generating &&
														canShareWordDoc ? (
															<GeneratedItineraryAsset
																id={itinerary.id}
																onChange={() => revalidate()}
																fileUrl={itinerary.word_doc_file_url}
																regenerateLabel={
																	<Box display="inline">
																		<Icons.Refresh /> Regenerate Files
																	</Box>
																}
																downloadLabel={
																	<>
																		<Icons.DocumentDownload /> Download Word
																		Docx
																	</>
																}
																target={undefined}
																download
																align="left"
															/>
														) : null}
														{canSharePDF || canShareWordDoc ? (
															<ItineraryPDFRegenrateWithSettings
																itinerary={itinerary}
																onChange={() => revalidate()}
																itineraryPDFRegenrateWithSettingsPopoverRef={
																	pdfSettingsPopoverRef
																}
															/>
														) : null}
													</>
												) : null}
											</Stack>
										)
									}}
								>
									<Button level="tertiary" size="sm">
										<Icons.DotsVertical />
									</Button>
								</Popover>
							) : null}
						</Inline>
					</Box>
				</Inline>
			</Stack>
			<Stack borderWidth="1" padding="2" rounded="md" gap="2" marginY="4">
				<Box>
					{joinAttributes(
						<Box as="span">
							<Box display="inlineBlock" marginRight="2">
								<Icons.Calendar color="muted" />
							</Box>
							<Time
								value={localOrUtcTimestampToLocalDate(
									start_date_local,
									start_date
								)}
							/>{" "}
							for{" "}
							<TimeDuration value={createDuration(days, "days")}>
								{pluralize("Day", days, true)}
							</TimeDuration>
						</Box>,
						<Box as="span">
							<Box display="inlineBlock" marginRight="2">
								<Icons.UserGroup color="muted" />
							</Box>
							{quote.pax_string}
							{quote.complimentary_count > 0
								? ` (${quote.complimentary_count} FOC)`
								: null}
						</Box>
					)}
				</Box>
				{diffFromTrip ? (
					<Box color="warning" fontSize="sm" title={diffFromTrip.message}>
						<Icons.AttentionSolid /> The quote has different basic details than
						trip
					</Box>
				) : null}
			</Stack>
			<Stack position="relative" gap="6">
				<Heading as="h4">Services</Heading>
				{allOptions.map((tripQuote, index, arr) => {
					const { quote, trip } = tripQuote
					const { hotels, hotel_extras, start_date } = quote
					return (
						<Stack key={tripQuote.id} gap="2">
							{arr.length > 1 ? (
								<Box>
									<Box
										display="inlineBlock"
										bgColor="emphasis"
										color="on_emphasis"
										padding="2"
										rounded="md"
									>
										<Heading fontSize="md">
											Option {index + 1}: {tripQuote.name}
										</Heading>
									</Box>
								</Box>
							) : null}
							<QuoteHotels
								hotels={hotels}
								hotelExtras={hotel_extras}
								hidePrice={tripQuote.readonly || !canViewAccommocationCosting}
								startDateUTC={start_date}
								tripDestinations={trip?.destinations}
							/>
						</Stack>
					)
				})}
				<QuoteTransportsAndActivityTickets
					transports={cabs}
					usedCabTypes={used_cab_types}
					cabsPriceCalculations={cabs_price_calculations}
					transportExtras={transport_extras}
					travelActivities={travel_activities}
					hidePrice={tripQuote.readonly || !canViewTranportationActivityCosting}
					startDateUTC={start_date}
					taxPercentage={cabs_tax_percentage}
					children={children}
					noOfAdults={no_of_adults}
				/>
				<QuoteFlights
					flightsDetails={transformFlightsDetailsToPreview(flights)}
					hidePrice={tripQuote.readonly || !canViewTotalCosting}
					flightPrice={flights}
					startDateUTC={start_date}
				/>
				<QuoteVisas
					visas={visas}
					hidePrice={tripQuote.readonly || !canViewTotalCosting}
					startDate={localOrUtcTimestampToLocalDate(
						start_date_local,
						start_date
					)}
				/>
				<QuoteExtras
					extras={other_extras}
					hidePrice={tripQuote.readonly || !canViewTotalCosting}
					startDateUTC={start_date}
				/>
			</Stack>
			{!hideItineary ? (
				<>
					<Divider />
					<ItineraryItem
						quote={tripQuote}
						editable={!readOnly && !shared_with_tenant}
						onChange={() => revalidate()}
					/>
				</>
			) : null}
		</Box>
	)
}

function ItineraryPDFRegenrateWithSettings({
	itinerary,
	onChange,
	itineraryPDFRegenrateWithSettingsPopoverRef,
}: {
	itinerary: ITripQuote["itinerary"] | undefined
	onChange: () => void
	itineraryPDFRegenrateWithSettingsPopoverRef: React.RefObject<HTMLDivElement | null>
}) {
	const { data } = useSWR("/trip-quote-pdf-templates", () =>
		xhr
			.get<{
				data: Array<TTripQuotePdfTemplate>
				meta: TBrandWiseColors
			}>("/trip-quote-pdf-templates")
			.then((resp) => resp.data)
	)
	if (!itinerary) return null

	const fileSettings = itinerary.file_settings ? itinerary.file_settings : {}

	const settingsToShow = QUOTE_SPECIAL_PDF_SETTINGS.filter((setting) => {
		// Checking in file_settings
		if (fileSettings[setting.key as keyof typeof fileSettings] !== undefined)
			return true
		// Checking in templates from API
		if (!data?.data) return false
		return (data.data || []).some((template) => {
			// Helper function to check if a setting exists and is truthy in a section
			const checkSection = (section: Record<string, unknown> | undefined) => {
				if (!section) return undefined
				const value = section[setting.key as keyof typeof section]
				return value !== undefined ? isTruthy(value) : undefined
			}

			// Check hotel_section_settings
			const hotelSetting = checkSection(template.hotel_section_settings)
			if (hotelSetting !== undefined) return hotelSetting

			// Check pricing_section_settings
			const pricingSetting = checkSection(template.pricing_section_settings)
			if (pricingSetting !== undefined) return pricingSetting

			return false
		})
	})

	if (!(settingsToShow || []).length) {
		return (
			<GeneratedItineraryAsset
				id={itinerary.id}
				isGenerating={itinerary.is_generating}
				onChange={onChange}
				fileUrl={undefined}
				level="tertiary"
				regenerateLabel={
					<Box display="inline">
						<Icons.Refresh /> Regenerate Files
					</Box>
				}
			/>
		)
	}

	const initialState = Object.fromEntries(
		(settingsToShow || []).map((setting) => [
			setting.key,
			isTruthy(fileSettings[setting.key as keyof typeof fileSettings]) ? 1 : 0,
		])
	)

	return (
		<Popover
			trigger={"click"}
			placement="bottom-end"
			interactive
			content={({ hide: hidePdfSettings }) => {
				return (
					<div ref={itineraryPDFRegenrateWithSettingsPopoverRef}>
						<PDFSettingsPopoverContentInline
							itineraryId={itinerary.id}
							isGenerating={itinerary.is_generating}
							fileUrl={undefined}
							onChange={() => {
								onChange()
								hidePdfSettings()
							}}
							regenerateLabel="Generate PDF"
							settingsToShow={settingsToShow}
							initialState={initialState}
						/>
					</div>
				)
			}}
		>
			{itinerary.is_generating ? (
				<Box
					display={"flex"}
					alignItems={"center"}
					justifyContent={"center"}
					width={"full"}
					height={"full"}
				>
					<Spinner />
				</Box>
			) : (
				<Button level="tertiary">
					<Icons.Refresh /> Regenerate Files
				</Button>
			)}
		</Popover>
	)
}

function PDFSettingsPopoverContentInline({
	itineraryId,
	isGenerating,
	fileUrl,
	onChange,
	regenerateLabel,
	settingsToShow = [],
	initialState = {},
}: {
	itineraryId: number
	isGenerating?: boolean
	fileUrl?: string
	onChange: () => void
	regenerateLabel?: React.ReactNode
	settingsToShow?: Array<{ key: string; label: string }>
	initialState?: Record<string, number>
}) {
	if (isGenerating) {
		return null
	}

	return (
		<Component initialState={initialState}>
			{({ state: pdfSettings, setState: setPdfSettings }) => (
				<Stack
					padding="4"
					gap="3"
					style={{
						minWidth: "220px",
					}}
				>
					<Box fontWeight="semibold">PDF File Preferences</Box>
					{settingsToShow.map((setting) => {
						const hideQuotePriceEnabled = isTruthy(pdfSettings.hide_quote_price)
						const isComponentWiseSetting =
							setting.key === "show_component_wise_prices"
						const isDisabled = hideQuotePriceEnabled && isComponentWiseSetting
						return (
							<Box as="label" display="block" key={setting.key}>
								<CheckboxInput
									checked={isTruthy(pdfSettings[setting.key])}
									disabled={isDisabled}
									onChange={() => {
										if (isDisabled) return
										const nextValue = isTruthy(pdfSettings[setting.key]) ? 0 : 1
										const updatedSettings = {
											...pdfSettings,
											[setting.key]: nextValue,
										}
										if (setting.key === "hide_quote_price" && nextValue === 1) {
											updatedSettings.show_component_wise_prices = 0
										}
										setPdfSettings(updatedSettings)
									}}
								/>
								<Box
									as="span"
									marginLeft="2"
									color={isDisabled ? "muted" : "black"}
									fontSize="sm"
									cursor={isDisabled ? "disabled" : "pointer"}
								>
									{setting.label}
								</Box>
							</Box>
						)
					})}
					<GeneratedItineraryAsset
						id={itineraryId}
						isGenerating={isGenerating}
						fileUrl={fileUrl}
						onChange={onChange}
						pdfSettings={pdfSettings}
						regenerateLabel={regenerateLabel}
					/>
				</Stack>
			)}
		</Component>
	)
}

export function QuoteHotels({
	hotels,
	hotelExtras,
	hidePrice,
	startDateUTC,
	tripDestinations,
}: {
	hotels:
		| undefined
		| Array<IQuote["hotels"][number] & { fetching_prices?: boolean | 0 | 1 }>
	hotelExtras: $PropertyType<IQuote, "hotel_extras"> | undefined
	hidePrice?: boolean
	startDateUTC: string
	tripDestinations?: TTripDestination[]
}) {
	const sectionRef = useRef<HTMLDivElement>(null)

	const hotelNotes = useMemo(() => {
		if (hotels && hotels.some((hotel) => hotel.notes?.length)) {
			return filterHotelNotes({
				hotels,
				tripDestination: tripDestinations,
			})
		}
		return []
	}, [hotels, tripDestinations])

	const [totalGivenPrice] = useMemo(() => {
		return (
			[] as Array<{
				given_price?: number
				currency: string
				calculated_price?: number
				similar_hotel_options?: Array<{
					calculated_price?: number
					given_price?: number
				}>
			}>
		)
			.concat(hotels || [])
			.concat(hotelExtras || [])
			.reduce<[Array<TMoney>, Array<TMoney>]>(
				(
					[totalGivenPrice, totalCalculatedPrice],
					{ given_price, calculated_price, similar_hotel_options, currency }
				) => {
					const [given, calculated] = similar_hotel_options?.length
						? similar_hotel_options.reduce<[number, number | undefined]>(
								([maxPrice, rateOfMaxOne], h) => {
									const max = Math.max(maxPrice, h.given_price || 0)
									return [
										max,
										max > maxPrice ? h.calculated_price : rateOfMaxOne,
									]
								},
								[given_price || 0, calculated_price]
							)
						: [given_price, calculated_price]
					return [
						addMoneyFromDifferentCurrencies(
							totalGivenPrice.concat([
								moneyParseByDecimal(given || 0, currency),
							])
						),
						addMoneyFromDifferentCurrencies(
							totalCalculatedPrice.concat([
								moneyParseByDecimal(calculated || 0, currency),
							])
						),
					]
				},
				[[], []]
			)
	}, [hotels, hotelExtras])
	return hotels?.length || hotelExtras?.length ? (
		<Box as="section" ref={sectionRef}>
			<Box display="flex" overflow="auto">
				<Box display={{ xs: "none", sm: "block" }}>
					<SectionIcon>
						<Icons.Bed size="6" />
					</SectionIcon>
				</Box>
				<Stack gap="4" width="full" flex="1" minWidth="0">
					{hotels?.length ? (
						<Box>
							<Box display="flex" marginBottom="2" alignItems="center">
								<Box display={{ xs: "block", sm: "none" }} marginRight="2">
									<SectionIcon>
										<Icons.Bed size="6" />
									</SectionIcon>
								</Box>
								<Heading as="h5" fontSize="md">
									Accommodation
								</Heading>
							</Box>
							<Box display={{ xs: "block", sm: "none" }}>
								<Box as="ol" marginBottom="8">
									{hotels.map((quoteHotel) => {
										const {
											hotel,
											date,
											date_local,
											meal_plan,
											room_type,
											no_of_rooms,
											persons_per_room,
											comments,
											currency,
											fetching_prices,
											calculated_price,
											given_price,
											adults_with_extra_bed,
											children_with_extra_bed,
											children_without_extra_bed,
											similar_hotel_options,
											room_configuration,
											per_room_given_price,
											per_adult_with_extra_bed_given_price,
											per_child_with_extra_bed_given_price,
											per_child_without_extra_bed_given_price,
											per_room_calculated_price,
											per_adult_with_extra_bed_calculated_price,
											per_child_with_extra_bed_calculated_price,
											per_child_without_extra_bed_calculated_price,
										} = quoteHotel
										const [
											maxPriceWithOptions,
											maxCalculatedPriceWithOptions,
											givenPriceBifurcationOfMaxOne,
											calculatedPriceBifurcationOfMaxOne,
											roomConfigOfMaxOne,
										] = similar_hotel_options?.length
											? similar_hotel_options.reduce<
													[
														number,
														number | undefined,
														{
															per_room_given_price?: number
															per_adult_with_extra_bed_given_price?: number
															per_child_with_extra_bed_given_price?: number
															per_child_without_extra_bed_given_price?: number
														},
														{
															per_room_calculated_price?: number
															per_adult_with_extra_bed_calculated_price?: number
															per_child_with_extra_bed_calculated_price?: number
															per_child_without_extra_bed_calculated_price?: number
														},
														{
															noOfRooms: number
															adultsWithExtraBed: number
															childrenWithExtraBed: number
															childrenWithoutExtraBed: number
														},
													]
												>(
													(
														[
															maxPrice,
															rateOfMaxOne,
															givenPriceBirfucationOfMaxOne,
															calculatedPriceBirfucationOfMaxOne,
															roomConfigOfMaxOne,
														],
														h
													) => {
														const max = Math.max(maxPrice, h.given_price)
														const {
															no_of_rooms,
															adults_with_extra_bed,
															children_with_extra_bed,
															children_without_extra_bed,
															per_room_given_price,
															per_adult_with_extra_bed_given_price,
															per_child_with_extra_bed_given_price,
															per_child_without_extra_bed_given_price,
															per_room_calculated_price,
															per_adult_with_extra_bed_calculated_price,
															per_child_with_extra_bed_calculated_price,
															per_child_without_extra_bed_calculated_price,
														} = h
														return [
															max,
															max > maxPrice
																? h.calculated_price
																: rateOfMaxOne,
															max > maxPrice
																? {
																		per_room_given_price,
																		per_adult_with_extra_bed_given_price,
																		per_child_with_extra_bed_given_price,
																		per_child_without_extra_bed_given_price,
																	}
																: givenPriceBirfucationOfMaxOne,
															max > maxPrice
																? {
																		per_room_calculated_price,
																		per_adult_with_extra_bed_calculated_price,
																		per_child_with_extra_bed_calculated_price,
																		per_child_without_extra_bed_calculated_price,
																	}
																: calculatedPriceBirfucationOfMaxOne,
															max > maxPrice
																? {
																		noOfRooms: no_of_rooms,
																		adultsWithExtraBed: adults_with_extra_bed,
																		childrenWithExtraBed:
																			children_with_extra_bed,
																		childrenWithoutExtraBed:
																			children_without_extra_bed,
																	}
																: roomConfigOfMaxOne,
														]
													},
													[
														given_price,
														calculated_price,
														{
															per_room_given_price,
															per_adult_with_extra_bed_given_price,
															per_child_with_extra_bed_given_price,
															per_child_without_extra_bed_given_price,
														},
														{
															per_room_calculated_price,
															per_adult_with_extra_bed_calculated_price,
															per_child_with_extra_bed_calculated_price,
															per_child_without_extra_bed_calculated_price,
														},
														{
															noOfRooms: no_of_rooms,
															adultsWithExtraBed: adults_with_extra_bed,
															childrenWithExtraBed: children_with_extra_bed,
															childrenWithoutExtraBed:
																children_without_extra_bed,
														},
													]
												)
											: [
													given_price,
													calculated_price,
													{
														per_room_given_price,
														per_adult_with_extra_bed_given_price,
														per_child_with_extra_bed_given_price,
														per_child_without_extra_bed_given_price,
													},
													{
														per_room_calculated_price,
														per_adult_with_extra_bed_calculated_price,
														per_child_with_extra_bed_calculated_price,
														per_child_without_extra_bed_calculated_price,
													},
													{
														noOfRooms: no_of_rooms,
														adultsWithExtraBed: adults_with_extra_bed,
														childrenWithExtraBed: children_with_extra_bed,
														childrenWithoutExtraBed: children_without_extra_bed,
													},
												]
										const isUsingSystemRates =
											Number(maxPriceWithOptions) &&
											Number(maxPriceWithOptions) ===
												Number(maxCalculatedPriceWithOptions)
										return (
											<Box
												as="li"
												key={quoteHotel.id}
												marginBottom="4"
												rounded="lg"
												overflow="hidden"
												borderWidth="1"
											>
												<Inline padding="4" bgColor="subtle" gap="4">
													<Stack style={{ width: "36px" }}>
														<Text fontWeight="semibold">
															{withOrdinalSuffix(
																getDiff(
																	utcTimestampToLocalDate(date),
																	utcTimestampToLocalDate(startDateUTC),
																	"day"
																) + 1
															)}{" "}
														</Text>
														<Text color="muted" fontSize="sm">
															Night
														</Text>
													</Stack>
													<Stack flex="1" minWidth="0" gap="1">
														<Stack>
															<Text fontWeight="semibold">{hotel.name}</Text>
															<Text fontSize="sm">
																{joinAttributes(
																	hotel.location.short_name,
																	hotel.stars ? (
																		<Stars stars={hotel.stars} />
																	) : null,
																	<EntryTypeIcon
																		entryType={hotel.entry_type}
																		createdBy={hotel?.created_by?.name}
																	/>,
																	hotel.deleted_at ? (
																		<Badge warning>Disabled</Badge>
																	) : null
																)}
															</Text>
														</Stack>
														{similar_hotel_options?.length ? (
															<Box fontSize="sm">
																{similar_hotel_options?.map(
																	(similarQuoteHotel) => (
																		<Box
																			key={similarQuoteHotel.hotel.id}
																			paddingLeft="4"
																			position="relative"
																		>
																			<Box position="absolute" left="0">
																				/
																			</Box>
																			<Box fontWeight="semibold">
																				{similarQuoteHotel.hotel.name}
																				{similarQuoteHotel.hotel
																					.stars_string ? (
																					<Box
																						as="span"
																						fontSize="xs"
																						marginLeft="1"
																					>
																						{
																							similarQuoteHotel.hotel
																								.stars_string
																						}
																					</Box>
																				) : null}
																				{similarQuoteHotel.hotel.deleted_at ? (
																					<Badge warning>Disabled</Badge>
																				) : null}
																			</Box>
																			<Box fontSize="xs">
																				{joinAttributes(
																					similarQuoteHotel.hotel.location
																						.name !== hotel.location.name
																						? similarQuoteHotel.hotel.location
																								.short_name
																						: null,
																					similarQuoteHotel.meal_plan.id !==
																						meal_plan.id ? (
																						<>
																							{similarQuoteHotel.meal_plan.name}
																						</>
																					) : null,
																					similarQuoteHotel.room_type.id !==
																						room_type.id ||
																						similarQuoteHotel.no_of_rooms !==
																							no_of_rooms ||
																						similarQuoteHotel.persons_per_room !==
																							persons_per_room ? (
																						<>
																							{similarQuoteHotel.no_of_rooms}{" "}
																							{similarQuoteHotel.room_type.name}{" "}
																							(
																							{joinAttributes(
																								`${
																									similarQuoteHotel.no_of_rooms *
																									similarQuoteHotel.persons_per_room
																								}A`,
																								[
																									similarQuoteHotel.adults_with_extra_bed,
																									`${similarQuoteHotel.adults_with_extra_bed} AwEb`,
																								],
																								[
																									similarQuoteHotel.children_with_extra_bed,
																									`${similarQuoteHotel.children_with_extra_bed} CwEb`,
																								],
																								[
																									similarQuoteHotel.children_without_extra_bed,
																									`${similarQuoteHotel.children_without_extra_bed} CNB`,
																								]
																							)}
																							)
																						</>
																					) : null
																				)}
																			</Box>
																		</Box>
																	)
																)}
															</Box>
														) : null}
													</Stack>
												</Inline>
												<Inline
													paddingX="4"
													paddingY="2"
													bgColor="default"
													gap="4"
												>
													<Box style={{ width: "36px" }}>
														<Time
															timestamp={date}
															localTimestamp={date_local}
															format="D MMM"
															fontSize="sm"
															whiteSpace="preserve"
															fontWeight="semibold"
														/>
													</Box>
													<Stack flex="1" gap="1">
														<Inline gap="2">
															<Box>
																<Icons.Bed color="muted" />
															</Box>
															<Text fontWeight="semibold">
																{joinAttributes(
																	meal_plan.name,
																	`${no_of_rooms} ${room_type.name}`
																)}
															</Text>
														</Inline>
														<Inline justifyContent="between" gap="2">
															<Inline flex="1" alignItems="center" gap="2">
																<Box>
																	<Icons.UserGroup color="muted" />
																</Box>
																<Box>
																	<Text>
																		{room_configuration?.description_short ||
																			[
																				persons_per_room * no_of_rooms + " Pax",
																				adults_with_extra_bed
																					? `${adults_with_extra_bed} AwEb`
																					: "",
																				children_with_extra_bed
																					? `${children_with_extra_bed} CwEb`
																					: "",
																				children_without_extra_bed
																					? `${children_without_extra_bed} CNB`
																					: "",
																			]
																				.filter(Boolean)
																				.join(" + ")}
																	</Text>
																</Box>
															</Inline>
															{!hidePrice ? (
																<Inline alignItems="end">
																	<Box>
																		<Inline
																			gap="1"
																			alignItems="center"
																			fontWeight="semibold"
																		>
																			{isUsingSystemRates ? (
																				<Icons.Ok
																					color="success"
																					title="Using System Rates"
																				/>
																			) : null}
																			<Money
																				amount={maxPriceWithOptions}
																				currency={currency}
																				showCurrency
																				title={getPriceBirfucationAsText(
																					roomConfigOfMaxOne,
																					{
																						currency,
																						per_adult_with_extra_bed_price:
																							givenPriceBifurcationOfMaxOne.per_adult_with_extra_bed_given_price,
																						per_child_with_extra_bed_price:
																							givenPriceBifurcationOfMaxOne.per_child_with_extra_bed_given_price,
																						per_child_without_extra_bed_price:
																							givenPriceBifurcationOfMaxOne.per_child_without_extra_bed_given_price,
																						per_room_price:
																							givenPriceBifurcationOfMaxOne.per_room_given_price,
																					}
																				)}
																			/>
																		</Inline>
																	</Box>
																	{isUsingSystemRates ? null : (
																		<Text fontSize="xs" color="muted">
																			{" / "}
																			{maxCalculatedPriceWithOptions ? (
																				<Money
																					amount={maxCalculatedPriceWithOptions}
																					currency={currency}
																					title={
																						maxCalculatedPriceWithOptions &&
																						calculatedPriceBifurcationOfMaxOne.per_room_calculated_price
																							? getPriceBirfucationAsText(
																									roomConfigOfMaxOne,
																									{
																										currency,
																										per_adult_with_extra_bed_price:
																											calculatedPriceBifurcationOfMaxOne.per_adult_with_extra_bed_calculated_price,
																										per_child_with_extra_bed_price:
																											calculatedPriceBifurcationOfMaxOne.per_child_with_extra_bed_calculated_price,
																										per_child_without_extra_bed_price:
																											calculatedPriceBifurcationOfMaxOne.per_child_without_extra_bed_calculated_price,
																										per_room_price:
																											calculatedPriceBifurcationOfMaxOne.per_room_calculated_price,
																									}
																								)
																							: "System Rate"
																					}
																				/>
																			) : (
																				"N/A"
																			)}
																		</Text>
																	)}
																	{fetching_prices ? (
																		<Text>
																			<Badge warning>
																				<Icons.Refresh spin size="3" /> Rates
																			</Badge>
																		</Text>
																	) : null}
																</Inline>
															) : null}
														</Inline>
														{comments ? (
															<Inline
																alignItems="center"
																gap="2"
																title="Comments"
															>
																<Box>
																	<Icons.Annotation color="muted" />
																</Box>
																<Text fontSize="sm" color="muted">
																	{comments}
																</Text>
															</Inline>
														) : null}
													</Stack>
												</Inline>
											</Box>
										)
									})}
								</Box>
							</Box>
							<Box display={{ xs: "none", sm: "block" }}>
								<Table
									bordered
									responsive
									headers={["Night", "Hotel", "Meal", "Rooms"].concat(
										hidePrice ? [] : ["Price"]
									)}
									alignCols={!hidePrice ? { 4: "right" } : {}}
									rows={hotels.map((quoteHotel) => {
										const {
											hotel,
											date,
											date_local,
											meal_plan,
											room_type,
											no_of_rooms,
											persons_per_room,
											comments,
											currency,
											fetching_prices,
											calculated_price,
											given_price,
											adults_with_extra_bed,
											children_with_extra_bed,
											children_without_extra_bed,
											similar_hotel_options,
											room_configuration,
											per_room_given_price,
											per_adult_with_extra_bed_given_price,
											per_child_with_extra_bed_given_price,
											per_child_without_extra_bed_given_price,
											per_room_calculated_price,
											per_adult_with_extra_bed_calculated_price,
											per_child_with_extra_bed_calculated_price,
											per_child_without_extra_bed_calculated_price,
										} = quoteHotel
										const [
											maxPriceWithOptions,
											maxCalculatedPriceWithOptions,
											givenPriceBifurcationOfMaxOne,
											calculatedPriceBifurcationOfMaxOne,
											roomConfigOfMaxOne,
										] = similar_hotel_options?.length
											? similar_hotel_options.reduce<
													[
														number,
														number | undefined,
														{
															per_room_given_price?: number
															per_adult_with_extra_bed_given_price?: number
															per_child_with_extra_bed_given_price?: number
															per_child_without_extra_bed_given_price?: number
														},
														{
															per_room_calculated_price?: number
															per_adult_with_extra_bed_calculated_price?: number
															per_child_with_extra_bed_calculated_price?: number
															per_child_without_extra_bed_calculated_price?: number
														},
														{
															noOfRooms: number
															adultsWithExtraBed: number
															childrenWithExtraBed: number
															childrenWithoutExtraBed: number
														},
													]
												>(
													(
														[
															maxPrice,
															rateOfMaxOne,
															givenPriceBirfucationOfMaxOne,
															calculatedPriceBirfucationOfMaxOne,
															roomConfigOfMaxOne,
														],
														h
													) => {
														const max = Math.max(maxPrice, h.given_price)
														const {
															per_room_given_price,
															per_adult_with_extra_bed_given_price,
															per_child_with_extra_bed_given_price,
															per_child_without_extra_bed_given_price,
															per_room_calculated_price,
															per_adult_with_extra_bed_calculated_price,
															per_child_with_extra_bed_calculated_price,
															per_child_without_extra_bed_calculated_price,
														} = h
														return [
															max,
															max > maxPrice
																? h.calculated_price
																: rateOfMaxOne,
															max > maxPrice
																? {
																		per_room_given_price,
																		per_adult_with_extra_bed_given_price,
																		per_child_with_extra_bed_given_price,
																		per_child_without_extra_bed_given_price,
																	}
																: givenPriceBirfucationOfMaxOne,
															max > maxPrice
																? {
																		per_room_calculated_price,
																		per_adult_with_extra_bed_calculated_price,
																		per_child_with_extra_bed_calculated_price,
																		per_child_without_extra_bed_calculated_price,
																	}
																: calculatedPriceBirfucationOfMaxOne,
															max > maxPrice
																? {
																		noOfRooms: h.no_of_rooms,
																		adultsWithExtraBed: h.adults_with_extra_bed,
																		childrenWithExtraBed:
																			h.children_with_extra_bed,
																		childrenWithoutExtraBed:
																			h.children_without_extra_bed,
																	}
																: roomConfigOfMaxOne,
														]
													},
													[
														given_price,
														calculated_price,
														{
															per_room_given_price,
															per_adult_with_extra_bed_given_price,
															per_child_with_extra_bed_given_price,
															per_child_without_extra_bed_given_price,
														},
														{
															per_room_calculated_price,
															per_adult_with_extra_bed_calculated_price,
															per_child_with_extra_bed_calculated_price,
															per_child_without_extra_bed_calculated_price,
														},
														{
															noOfRooms: no_of_rooms,
															adultsWithExtraBed: adults_with_extra_bed,
															childrenWithExtraBed: children_with_extra_bed,
															childrenWithoutExtraBed:
																children_without_extra_bed,
														},
													]
												)
											: [
													given_price,
													calculated_price,
													{
														per_room_given_price,
														per_adult_with_extra_bed_given_price,
														per_child_with_extra_bed_given_price,
														per_child_without_extra_bed_given_price,
													},
													{
														per_room_calculated_price,
														per_adult_with_extra_bed_calculated_price,
														per_child_with_extra_bed_calculated_price,
														per_child_without_extra_bed_calculated_price,
													},
													{
														noOfRooms: no_of_rooms,
														adultsWithExtraBed: adults_with_extra_bed,
														childrenWithExtraBed: children_with_extra_bed,
														childrenWithoutExtraBed: children_without_extra_bed,
													},
												]
										const isUsingSystemRates =
											Number(maxPriceWithOptions) &&
											Number(maxPriceWithOptions) ===
												Number(maxCalculatedPriceWithOptions)
										const status = getRoomStatusOnDate(
											{
												black_out_date_intervals:
													room_type.black_out_date_intervals,
											},
											parseDate(date)
										)
										return [
											<Stack>
												<Text fontWeight="semibold">
													{withOrdinalSuffix(
														getDiff(
															utcTimestampToLocalDate(date),
															utcTimestampToLocalDate(startDateUTC),
															"day"
														) + 1
													)}
												</Text>
												<Text>
													<Time
														timestamp={date}
														localTimestamp={date_local}
														format="D MMM"
														whiteSpace="preserve"
														fontSize="sm"
														color="muted"
													/>
												</Text>
											</Stack>,
											<Stack gap="1">
												<Stack gap="px">
													<Box fontWeight="semibold">{hotel.name}</Box>
													<Inline fontSize="sm" alignItems={"center"} gap={"2"}>
														{hotel.location.short_name}
														{hotel.stars_string
															? `, ${hotel.stars_string}`
															: null}
														<EntryTypeIcon
															entryType={hotel.entry_type}
															createdBy={hotel?.created_by?.name}
														/>
														{hotel.deleted_at ? (
															<Badge warning>Disabled</Badge>
														) : null}
													</Inline>
												</Stack>
												{similar_hotel_options?.length ? (
													<Box fontSize="sm">
														{similar_hotel_options?.map((similarQuoteHotel) => (
															<Box
																key={similarQuoteHotel.hotel.id}
																position="relative"
																paddingLeft="4"
																marginTop="1"
															>
																<Box position="absolute" left="0">
																	/
																</Box>
																<Box fontWeight="semibold">
																	{similarQuoteHotel.hotel.name}{" "}
																	{similarQuoteHotel.hotel.stars_string ? (
																		<Box as="span" fontSize="xs" marginLeft="1">
																			{similarQuoteHotel.hotel.stars_string}
																		</Box>
																	) : null}{" "}
																	{similarQuoteHotel.hotel.deleted_at ? (
																		<Box marginLeft="1">
																			<Badge warning>Disabled</Badge>
																		</Box>
																	) : null}
																</Box>
																<Box fontSize="xs">
																	{joinAttributes(
																		similarQuoteHotel.hotel.location.name !==
																			hotel.location.name
																			? similarQuoteHotel.hotel.location
																					.short_name
																			: null,
																		similarQuoteHotel.meal_plan.id !==
																			meal_plan.id ? (
																			<>{similarQuoteHotel.meal_plan.name}</>
																		) : null,
																		similarQuoteHotel.room_type.id !==
																			room_type.id ||
																			similarQuoteHotel.no_of_rooms !==
																				no_of_rooms ||
																			similarQuoteHotel.persons_per_room !==
																				persons_per_room ? (
																			<>
																				{similarQuoteHotel.no_of_rooms}{" "}
																				{similarQuoteHotel.room_type.name} (
																				{joinAttributes(
																					`${
																						similarQuoteHotel.no_of_rooms *
																						similarQuoteHotel.persons_per_room
																					}A`,
																					[
																						similarQuoteHotel.adults_with_extra_bed,
																						`${similarQuoteHotel.adults_with_extra_bed} AwEb`,
																					],
																					[
																						similarQuoteHotel.children_with_extra_bed,
																						`${similarQuoteHotel.children_with_extra_bed} CwEb`,
																					],
																					[
																						similarQuoteHotel.children_without_extra_bed,
																						`${similarQuoteHotel.children_without_extra_bed} CNB`,
																					]
																				)}
																				)
																			</>
																		) : null
																	)}
																</Box>
															</Box>
														))}
													</Box>
												) : null}
												{comments ? (
													<Box
														maxWidth="lg"
														color="muted"
														fontSize="sm"
														title="Comment"
													>
														<Icons.Annotation opacity="50" size="4" />{" "}
														{comments}
													</Box>
												) : null}
											</Stack>,
											meal_plan.name,
											<Stack gap="px">
												<Text
													fontWeight="semibold"
													title={`${persons_per_room} Persons per Room`}
												>
													{no_of_rooms}{" "}
													{room_type.display_name || room_type.name}
													{room_type.short_name ? (
														<Text as="span" color="muted" fontWeight="normal">
															{` `}[[{room_type.short_name}]]
														</Text>
													) : null}{" "}
													{status === "stop_sale" && (
														<Badge danger>Unavailable</Badge>
													)}
													{status === "blackout" && (
														<Badge warning>High Demand</Badge>
													)}
												</Text>

												<Text fontSize="sm" color="muted">
													{room_configuration?.description_short ||
														[
															`${persons_per_room * no_of_rooms} Pax`,
															adults_with_extra_bed
																? `${adults_with_extra_bed} AwEB`
																: "",
															children_with_extra_bed
																? `${children_with_extra_bed} CwEB`
																: "",
															children_without_extra_bed
																? `${children_without_extra_bed} CNB`
																: "",
														]
															.filter(Boolean)
															.join(" + ")}
												</Text>
											</Stack>,
										].concat(
											hidePrice
												? []
												: [
														<Stack alignItems="end" gap="1">
															<Inline
																gap="1"
																alignItems="center"
																fontWeight="semibold"
															>
																{isUsingSystemRates ? (
																	<Icons.Ok
																		color="success"
																		title="Using System Rates"
																	/>
																) : null}
																<Money
																	amount={maxPriceWithOptions}
																	currency={currency}
																	showCurrency
																	title={getPriceBirfucationAsText(
																		roomConfigOfMaxOne,
																		{
																			currency,
																			per_adult_with_extra_bed_price:
																				givenPriceBifurcationOfMaxOne.per_adult_with_extra_bed_given_price,
																			per_child_with_extra_bed_price:
																				givenPriceBifurcationOfMaxOne.per_child_with_extra_bed_given_price,
																			per_child_without_extra_bed_price:
																				givenPriceBifurcationOfMaxOne.per_child_without_extra_bed_given_price,
																			per_room_price:
																				givenPriceBifurcationOfMaxOne.per_room_given_price,
																		}
																	)}
																/>
															</Inline>
															{isUsingSystemRates ? null : (
																<Box fontSize="xs" color="muted">
																	/{" "}
																	{maxCalculatedPriceWithOptions ? (
																		<Money
																			amount={maxCalculatedPriceWithOptions}
																			currency={currency}
																			title={
																				maxCalculatedPriceWithOptions &&
																				calculatedPriceBifurcationOfMaxOne.per_room_calculated_price
																					? getPriceBirfucationAsText(
																							roomConfigOfMaxOne,
																							{
																								currency,
																								per_adult_with_extra_bed_price:
																									calculatedPriceBifurcationOfMaxOne.per_adult_with_extra_bed_calculated_price,
																								per_child_with_extra_bed_price:
																									calculatedPriceBifurcationOfMaxOne.per_child_with_extra_bed_calculated_price,
																								per_child_without_extra_bed_price:
																									calculatedPriceBifurcationOfMaxOne.per_child_without_extra_bed_calculated_price,
																								per_room_price:
																									calculatedPriceBifurcationOfMaxOne.per_room_calculated_price,
																							}
																						)
																					: "System Rate"
																			}
																		/>
																	) : (
																		"N/A"
																	)}
																</Box>
															)}
															{fetching_prices ? (
																<Text>
																	<Badge warning>
																		<Icons.Refresh spin size="3" /> Rates
																	</Badge>
																</Text>
															) : null}
														</Stack>,
													]
										)
									})}
								/>
							</Box>
							{/* hotel notes */}
							<Box paddingTop={"4"}>
								{hotelNotes.length > 0 ? (
									<>
										<Box padding={"2"}>
											<Text fontWeight="semibold" fontSize={"md"}>
												Notes
											</Text>
										</Box>
										<Component initialState={false}>
											{({ state, setState }) => {
												const collapsed = !state
												// showing first hotel -> first note -> first description less then 200 characters . To see more notes he can toggle the button
												//first hotel
												const visibleHotels = collapsed
													? [hotelNotes[0]]
													: hotelNotes
												let showNotesToggle = false

												return (
													<Box
														borderWidth="1"
														rounded="md"
														backgroundColor="subtle"
														overflow="hidden"
													>
														<Stack gap="2" rounded="md" bgColor="default">
															<Stack gap="1">
																<Box>
																	{visibleHotels.map(
																		({ hotel, notes }, index) => {
																			//first note
																			const visibleNotes = collapsed
																				? [notes[0]]
																				: notes
																			return (
																				<Stack key={index} gap={"2"}>
																					<Box
																						padding={"1"}
																						backgroundColor={"subtle"}
																						borderBottomWidth="1"
																						borderColor={"emphasis"}
																					>
																						<Text
																							fontWeight="semibold"
																							fontSize={"md"}
																						>
																							{hotel}
																						</Text>
																					</Box>

																					{visibleNotes.map(
																						({ key, descriptions }) => {
																							//first description
																							const viewDescription = collapsed
																								? [descriptions[0]]
																								: descriptions
																							showNotesToggle =
																								hotelNotes.length > 1 ||
																								notes.length > 1 ||
																								descriptions.length > 1 ||
																								descriptions.some(
																									(description) =>
																										description.length > 200
																								)
																							return (
																								<Box
																									paddingLeft={"2"}
																									key={key}
																								>
																									<Text
																										fontWeight="semibold"
																										color="accent"
																									>
																										{key}:
																									</Text>

																									{viewDescription.map(
																										(description, idx) => {
																											// description less then 200 characters
																											const text =
																												collapsed &&
																												description.length > 200
																													? description.slice(
																															0,
																															200
																														) + "..."
																													: description
																											return (
																												<Markdown key={idx}>
																													{text}
																												</Markdown>
																											)
																										}
																									)}
																								</Box>
																							)
																						}
																					)}
																				</Stack>
																			)
																		}
																	)}
																</Box>
															</Stack>

															{showNotesToggle ? (
																<Box paddingX={"2"}>
																	<Button
																		title="notes"
																		inline
																		onClick={() => {
																			if (state)
																				sectionRef.current?.scrollIntoView({
																					behavior: "smooth",
																				})
																			setState(!state)
																		}}
																	>
																		<Text
																			as="span"
																			fontWeight="semibold"
																			color="primary"
																		>
																			{state ? "Hide Notes" : "Show Notes"}
																		</Text>
																		<Text as="span">
																			<Icons.ChevronDown
																				rotate={state ? "180" : undefined}
																				transition="1"
																			/>
																		</Text>
																	</Button>
																</Box>
															) : null}
														</Stack>
													</Box>
												)
											}}
										</Component>
									</>
								) : null}
							</Box>
						</Box>
					) : null}
					{hotelExtras?.length ? (
						<Box>
							<Heading as="h6" fontSize="md">
								Hotel Special Inclusions
							</Heading>
							<Box display={{ xs: "block", sm: "none" }}>
								<Box as="ol" marginBottom="8">
									{hotelExtras.map(
										({
											id,
											service,
											date,
											date_local,
											hotel,
											given_price,
											currency,
											comments,
										}) => (
											<Box
												as="li"
												key={id}
												marginBottom="4"
												rounded="lg"
												overflow="hidden"
												bgColor="default"
												borderWidth="1"
											>
												<Inline padding="4" gap="4">
													{date ? (
														<Stack style={{ width: "36px" }} gap="2">
															<Stack>
																<Text fontWeight="semibold">
																	{withOrdinalSuffix(
																		getDiff(
																			utcTimestampToLocalDate(date),
																			utcTimestampToLocalDate(startDateUTC),
																			"day"
																		) + 1
																	)}
																</Text>
																<Text color="muted" fontSize="sm">
																	Day
																</Text>
															</Stack>
															<Box>
																<Time
																	timestamp={date}
																	localTimestamp={date_local}
																	format="D MMM"
																	fontSize="sm"
																	whiteSpace="preserve"
																	fontWeight="semibold"
																/>
															</Box>
														</Stack>
													) : null}
													<Stack flex="1" minWidth="0" gap="2">
														{hotel ? (
															<Stack>
																<Text fontWeight="semibold">{hotel.name}</Text>
																<Text fontSize="sm">
																	{joinAttributes(
																		hotel.location.short_name,
																		hotel.stars ? (
																			<Stars stars={hotel.stars} />
																		) : null
																	)}
																</Text>
															</Stack>
														) : null}
														<Inline gap="4" justifyContent="between">
															<Text fontWeight="semibold">{service.name}</Text>
															{!hidePrice ? (
																<Money
																	fontWeight="semibold"
																	amount={given_price || 0}
																	currency={currency}
																	showCurrency
																/>
															) : null}
														</Inline>
														{comments ? (
															<Text maxWidth="lg" color="muted" fontSize="sm">
																<Icons.Annotation /> {comments}
															</Text>
														) : null}
													</Stack>
												</Inline>
											</Box>
										)
									)}
								</Box>
							</Box>
							<Box display={{ xs: "none", sm: "block" }}>
								<Table
									bordered
									responsive
									headers={["Night", "Hotel", "Service"].concat(
										hidePrice ? [] : ["Price"]
									)}
									alignCols={{ 3: "right" }}
									rows={hotelExtras.map(
										({
											service,
											date,
											date_local,
											hotel,
											given_price,
											currency,
											comments,
										}) =>
											[
												date ? (
													<Stack>
														<Text fontWeight="semibold">
															{withOrdinalSuffix(
																getDiff(
																	utcTimestampToLocalDate(date),
																	utcTimestampToLocalDate(startDateUTC),
																	"day"
																) + 1
															)}
														</Text>
														<Box>
															<Time
																timestamp={date}
																localTimestamp={date_local}
																format="D MMM"
																whiteSpace="preserve"
																fontSize="sm"
															/>
														</Box>
													</Stack>
												) : null,
												hotel && (
													<Stack>
														<Text fontWeight="semibold">{hotel.name}</Text>
														<Text fontSize="sm" color="muted">
															{hotel.location.short_name}
															{hotel.stars_string
																? `, ${hotel.stars_string}`
																: null}
														</Text>
													</Stack>
												),
												<Box>
													<Box fontWeight="semibold">{service.name}</Box>
													{comments ? (
														<Box as="blockquote" maxWidth="lg">
															{comments}
														</Box>
													) : null}
												</Box>,
											].concat(
												hidePrice
													? []
													: [
															<Money
																fontWeight="semibold"
																amount={given_price || 0}
																currency={currency}
																showCurrency
															/>,
														]
											)
									)}
								/>
							</Box>
						</Box>
					) : null}
				</Stack>
			</Box>
			{!hidePrice ? (
				<Box textAlign="right" marginTop="4">
					<Box
						display="inlineBlock"
						paddingX="4"
						paddingY="1"
						bgColor="subtle"
						borderWidth="1"
						rounded="md"
						fontWeight="semibold"
					>
						Total: <MoneySum money={totalGivenPrice} />
					</Box>
				</Box>
			) : null}
		</Box>
	) : null
}

export function QuoteTransportsAndActivityTickets({
	transports,
	usedCabTypes,
	cabsPriceCalculations,
	transportExtras,
	travelActivities,
	hidePrice,
	startDateUTC,
	taxPercentage,
	children,
	noOfAdults,
}: {
	transports: Array<IQuote["cabs"][number] & { fetching_prices?: boolean }>
	usedCabTypes: $PropertyType<IQuote, "used_cab_types">
	cabsPriceCalculations:
		| undefined
		| Array<
				Required<IQuote>["cabs_price_calculations"][number] & {
					fetching_prices?: boolean
				}
		  >
	transportExtras: $PropertyType<IQuote, "transport_extras">
	travelActivities: $PropertyType<IQuote, "travel_activities">
	hidePrice?: boolean
	startDateUTC: string
	taxPercentage?: number
	children: Array<{ age: number }> | undefined
	noOfAdults: number
}) {
	const { extraServiceWithoutDates, daywiseServices } = useMemo(() => {
		return getDaywiseTransportsAndActivitiesForQuote({
			transports,
			travelActivities,
			transportExtras,
		})
	}, [transports, travelActivities, transportExtras])

	const [
		totalGivenPrice,
		totalTransportsPrice,
		totalTransportExtrasPrice,
		totalTravelActivitiesPrice,
	] = useMemo(() => {
		function getTotal(
			services: Array<{
				given_price?: number
				currency: string
				calculated_price?: number
			}>
		) {
			return services.reduce<Array<TMoney>>(
				(totalGivenPrice, { given_price, currency }) => {
					return addMoneyFromDifferentCurrencies(
						totalGivenPrice.concat([
							moneyParseByDecimal(given_price || 0, currency),
						])
					)
				},
				[]
			)
		}
		const totalTransportsPrice = getTotal(transports)
		const totalTransportExtrasPrice = getTotal(transportExtras || [])
		const totalTravelActivitiesPrice = getTotal(
			travelActivities.flatMap((t) => t.ticket_tourist_configurations) || []
		)
		return [
			addMoneyFromDifferentCurrencies(
				totalTransportsPrice
					.concat(totalTransportExtrasPrice)
					.concat(totalTravelActivitiesPrice)
			),
			totalTransportsPrice,
			totalTransportExtrasPrice,
			totalTravelActivitiesPrice,
		]
	}, [transports, transportExtras, travelActivities])

	const hideCabTypesFromEachService = Boolean(
		usedCabTypes?.length && cabsPriceCalculations?.length
	)

	const hasMultipleComponentPrices =
		[
			totalTransportsPrice,
			totalTransportExtrasPrice,
			totalTravelActivitiesPrice,
		].filter((t) => t.length).length > 1
	return daywiseServices.length ||
		extraServiceWithoutDates.length ||
		travelActivities?.length ? (
		<Box as="section">
			<Stack gap="4">
				<Inline gap="2" alignItems="center">
					<Box>
						<SectionIcon>
							<Icons.Bus size="6" />
						</SectionIcon>
					</Box>
					<Heading as="h5" fontSize="md">
						Transportation and Activities
					</Heading>
				</Inline>
				<Stack gap="6" flex="1" minWidth="0" width="full">
					{hideCabTypesFromEachService ? (
						<Inline
							padding="4"
							gap="4"
							rounded="md"
							bgColor="accent"
							borderColor="accent"
							borderWidth="1"
						>
							<Text fontWeight="semibold">Transportation/Type:</Text>
							<Text>
								{usedCabTypes
									?.map(
										(t) =>
											`${t.no_of_cabs}-${t.cab_type.name}${
												t.cab_type.deleted_at ? ` [Disabled]` : ``
											}`
									)
									.join(" + ")}
							</Text>
						</Inline>
					) : null}
					{daywiseServices.map(
						({ date, date_local, transportsAndActivities, extras }) => {
							return (
								<Inline
									position="relative"
									gap="4"
									collapseBelow="sm"
									key={date}
								>
									<Box position="sticky" top="0" zIndex="10">
										<Box position="sticky" top="0" zIndex="10">
											<Box
												bgColor="default"
												textAlign="center"
												paddingX="4"
												paddingY="3"
												rounded="md"
												borderColor="white"
												style={{ minWidth: "110px" }}
											>
												<Text fontWeight="semibold" fontSize="md">
													{withOrdinalSuffix(
														getDiff(
															utcTimestampToLocalDate(date),
															utcTimestampToLocalDate(startDateUTC),
															"day"
														) + 1
													)}{" "}
													Day
												</Text>
												<Time
													timestamp={date}
													localTimestamp={date_local}
													format="ddd, D MMM"
													whiteSpace="preserve"
													fontSize="sm"
												/>
											</Box>
										</Box>
									</Box>
									<Box flex="1">
										<Stack gap="6">
											<Stack bgColor="default" rounded="md" borderWidth="1">
												{transportsAndActivities.map(
													({ type, transport, activity }, i) => {
														if (type === "transport") {
															const {
																transport_service,
																fetching_prices,
																cabs,
															} = transport
															const currency = cabs[0].currency
															const totalGivenPrice = cabs.reduce<number>(
																(total, { given_price }) =>
																	total + Number(given_price || 0),
																0
															)
															const startTime = cabs[0].start_time
															const durationTime = cabs[0].duration
															const durationFormatted =
																cabs[0].duration_formatted
															const endTime = cabs[0].end_time
															const totalCalculatedPrice = cabs.reduce<
																number | undefined
															>(
																(total, { calculated_price }) =>
																	calculated_price === undefined ||
																	calculated_price === null
																		? undefined
																		: Number(total || 0) +
																			Number(calculated_price || 0),
																undefined
															)
															const isUsingSystemRates =
																totalCalculatedPrice &&
																Number(totalCalculatedPrice) ===
																	Number(totalGivenPrice)
															return (
																<Inline
																	key={`${transport_service.id}-${i}`}
																	gap="6"
																	justifyContent="between"
																	padding="4"
																	borderTopWidth={i > 0 ? "1" : undefined}
																>
																	<Grid gap="4">
																		<Col
																			xs={12}
																			sm={hideCabTypesFromEachService ? 12 : 6}
																		>
																			<Stack gap="px">
																				<Text fontWeight="semibold">
																					{transport_service.from_to}
																				</Text>
																				<Inline gap="2">
																					<Text fontSize="sm">
																						{transport_service.service}
																					</Text>
																					<EntryTypeIcon
																						entryType={
																							transport_service.entry_type
																						}
																						createdBy={
																							transport_service?.created_by
																								?.name
																						}
																					/>
																				</Inline>

																				{startTime || durationTime ? (
																					<Text fontSize="sm" color="muted">
																						{joinAttributes(
																							startTime ? (
																								<>
																									Starts:{" "}
																									{formatDate(
																										parseDate(
																											startTime,
																											"HH:mm:ss"
																										),
																										"HH:mm"
																									)}{" "}
																									hrs
																								</>
																							) : null,
																							durationTime ? (
																								<>
																									Duration:{" "}
																									{durationFormatted
																										? durationFormatted
																										: formatDuration(
																												durationTime
																											)}{" "}
																									{startTime ? (
																										<>
																											(Ends:{" "}
																											{endTime
																												? formatDate(
																														parseDate(
																															endTime,
																															"HH:mm"
																														),
																														"HH:mm"
																													)
																												: formatDate(
																														addUnit(
																															parseDate(
																																startTime,
																																"HH:mm"
																															),
																															createDuration(
																																durationTime
																															).asMinutes(),
																															"minutes"
																														),
																														"HH:mm"
																													)}{" "}
																											hrs)
																										</>
																									) : null}
																								</>
																							) : null
																						)}
																					</Text>
																				) : null}
																				{transport_service.deleted_at ? (
																					<Box>
																						<Badge warning>
																							<Icons.Ban /> Disabled
																						</Badge>
																					</Box>
																				) : null}
																				{isTicketTypeClosedOnDate(
																					{
																						closed_on_date_intervals:
																							transport_service?.closed_on_date_intervals,
																						closed_on_days_of_week:
																							transport_service?.closed_on_days_of_week,
																					},
																					localOrUtcTimestampToLocalDate(
																						date_local,
																						date
																					)
																				) ? (
																					<Box>
																						<Badge danger>
																							<Icons.Ban /> Service Closed
																						</Badge>
																					</Box>
																				) : null}
																			</Stack>
																		</Col>
																		{hideCabTypesFromEachService ? null : (
																			<Col>
																				<Stack gap="1">
																					<Inline gap="4" flexWrap="wrap">
																						{cabs.map(
																							(
																								{
																									cab_type,
																									cab_locality,
																									no_of_cabs,
																									calculated_price,
																									given_price,
																									currency,
																								},
																								index
																							) => (
																								<Stack
																									key={`${transport_service.id}-${index}`}
																								>
																									<Stack>
																										<Text fontWeight="semibold">
																											{no_of_cabs}-
																											{cab_type.display_name
																												? cab_type.display_name
																												: cab_type.short_name
																													? null
																													: cab_type.name}
																											{cab_type.short_name ? (
																												<Text
																													as="span"
																													color="muted"
																													fontWeight="normal"
																												>
																													[[
																													{cab_type.short_name}
																													]]
																												</Text>
																											) : null}{" "}
																											{cab_type.deleted_at ? (
																												<Badge warning>
																													<Icons.Ban /> Disabled
																												</Badge>
																											) : null}
																										</Text>
																										<ShowCabCapacityInfo
																											cabType={cab_type}
																											showEntryType
																										/>
																										{cab_locality ? (
																											<Text fontSize="sm">
																												{cab_locality.name}
																											</Text>
																										) : null}
																									</Stack>
																									{hidePrice ||
																									cabsPriceCalculations?.length ? null : (
																										<Box fontSize="sm">
																											{given_price ? (
																												<Stack>
																													<Money
																														currency={currency}
																														color={
																															calculated_price &&
																															given_price ===
																																calculated_price
																																? "success"
																																: "muted"
																														}
																														amount={
																															given_price || 0
																														}
																													/>
																												</Stack>
																											) : (
																												<Text color="warning">
																													N/A
																												</Text>
																											)}
																										</Box>
																									)}
																								</Stack>
																							)
																						)}
																					</Inline>

																					{cabs ? (
																						<Box>
																							<Box display={"inlineBlock"}>
																								<CabCapacityWarningMessage
																									children={children}
																									messageType="withPax"
																									cabs={cabs}
																									noOfAdults={noOfAdults ?? 0}
																								/>
																							</Box>
																						</Box>
																					) : null}
																					{cabs?.[0]?.comments?.length ? (
																						<Text
																							fontSize="sm"
																							color="muted"
																							maxWidth="lg"
																						>
																							<Icons.Annotation />{" "}
																							{collect(
																								cabs
																									.map((c) => c.comments)
																									.filter((c): c is string =>
																										Boolean(c?.length)
																									)
																							)
																								.unique()
																								.toArray()
																								.join(". ")}
																						</Text>
																					) : null}
																				</Stack>
																			</Col>
																		)}
																	</Grid>
																	{!hidePrice &&
																	!cabsPriceCalculations?.length ? (
																		<Box
																			style={{ width: "60px" }}
																			textAlign="right"
																		>
																			<Stack gap="1" alignItems="end">
																				<Inline
																					gap="1"
																					alignItems="center"
																					fontWeight="semibold"
																				>
																					{isUsingSystemRates ? (
																						<Icons.Ok
																							color="success"
																							title="Using System Rate"
																						/>
																					) : null}
																					<Money
																						amount={totalGivenPrice || 0}
																						currency={currency}
																						showCurrency
																					/>
																				</Inline>
																				{isUsingSystemRates ? null : (
																					<Text
																						color="muted"
																						fontSize="xs"
																						title="System Rate"
																					>
																						/{" "}
																						{totalCalculatedPrice !==
																						undefined ? (
																							<Money
																								amount={totalCalculatedPrice}
																								currency={currency}
																							/>
																						) : (
																							"N/A"
																						)}
																					</Text>
																				)}
																				{fetching_prices ? (
																					<Text>
																						<Badge warning>
																							<Icons.Refresh spin size="3" />{" "}
																							Rates
																						</Badge>
																					</Text>
																				) : null}
																			</Stack>
																		</Box>
																	) : null}
																</Inline>
															)
														} else if (type === "activity") {
															return (
																<Box
																	padding="4"
																	key={`${activity.id}`}
																	borderTopWidth={i > 0 ? "1" : undefined}
																>
																	<QuoteTravelActivityListItem
																		activity={activity}
																		hidePrice={hidePrice}
																	/>
																</Box>
															)
														}
														return null
													}
												)}
											</Stack>
											{extras.length ? (
												<Stack gap="1">
													<Box paddingLeft="4">
														<Heading color="primary" fontSize="base">
															Other Inclusions
														</Heading>
													</Box>
													<Stack bgColor="default" rounded="md" borderWidth="1">
														{extras.map(
															({ service, given_price, currency }, i) => {
																return (
																	<Inline
																		gap="6"
																		justifyContent="between"
																		padding="4"
																		borderTopWidth={i > 0 ? "1" : undefined}
																		key={`${service.id}-${i}`}
																	>
																		<Box key={service.id}>
																			<Stack gap="px">
																				<Text fontWeight="semibold">
																					{service.name}
																				</Text>
																			</Stack>
																		</Box>
																		{!hidePrice ? (
																			<Box
																				style={{ width: "60px" }}
																				textAlign="right"
																			>
																				{given_price ? (
																					<Money
																						currency={currency}
																						amount={given_price}
																						fontWeight="semibold"
																						showCurrency
																					/>
																				) : null}
																			</Box>
																		) : null}
																	</Inline>
																)
															}
														)}
													</Stack>
												</Stack>
											) : null}
										</Stack>
									</Box>
								</Inline>
							)
						}
					)}
					{cabsPriceCalculations?.length && !hidePrice ? (
						<Inline
							padding="4"
							gap="4"
							rounded="md"
							bgColor="accent"
							borderColor="accent"
							borderWidth="1"
						>
							<Text fontWeight="semibold">Prices using Calculator:</Text>
							<Box as="ul">
								{cabsPriceCalculations?.map(
									({
										id,
										cab_type,
										no_of_cabs,
										currency,
										per_quantity_excluding_tax_given_price,
										given_price,
										metric_wise_prices,
										fetching_prices,
									}) => {
										return (
											<Box as="li" key={id}>
												<Text as="span" fontWeight="semibold">
													{no_of_cabs}-{cab_type.name}
												</Text>
												={" "}
												<Money
													amount={given_price || 0}
													currency={currency}
													showCurrency
													fontWeight="semibold"
												/>{" "}
												{!given_price ? null : (
													<Text as="span" color="muted">
														(
														{metric_wise_prices.map(
															(
																{
																	id,
																	metric,
																	given_quantity,
																	per_quantity_price,
																	per_quantity_given_price,
																	currency,
																},
																index
															) =>
																metric &&
																given_quantity &&
																per_quantity_given_price ? (
																	<Text as="span" key={id}>
																		{index > 0 ? " + " : null}
																		{numberToLocalString(given_quantity)} x{" "}
																		<Money
																			amount={per_quantity_given_price}
																			currency={currency}
																		/>{" "}
																		{Number(per_quantity_given_price) &&
																		Number(per_quantity_given_price) ===
																			Number(per_quantity_price) ? (
																			<>
																				<Icons.Ok
																					color="success"
																					title="Using System Rates"
																				/>{" "}
																			</>
																		) : null}
																		/ {metric.name}
																	</Text>
																) : null
														)}{" "}
														={" "}
														<Text as="span">
															<Money
																amount={per_quantity_excluding_tax_given_price}
																currency={currency}
															/>
															{taxPercentage
																? ` + ${numberToLocalString(taxPercentage)}%`
																: null}
														</Text>
														)
														{no_of_cabs > 1 ? (
															<Text as="span"> x {no_of_cabs}</Text>
														) : null}
													</Text>
												)}
												{fetching_prices ? (
													<Badge warning>
														<Icons.Refresh spin size="3" /> Rates
													</Badge>
												) : null}
											</Box>
										)
									}
								)}
							</Box>
						</Inline>
					) : null}
					{extraServiceWithoutDates.length ? (
						<Box>
							<Heading fontSize="md" as="h6">
								Other Transportation Inclusions
							</Heading>
							<Box>
								<Stack bgColor="default" rounded="md" borderWidth="1">
									{extraServiceWithoutDates.map(
										({ id, service, currency, given_price, comments }, i) => (
											<Inline
												key={id}
												gap="6"
												justifyContent="between"
												padding="4"
												borderTopWidth={i > 0 ? "1" : undefined}
											>
												<Stack>
													<Box>
														<Text fontWeight="semibold">{service.name}</Text>
													</Box>
													{comments ? (
														<Text maxWidth="lg" fontSize="sm" color="muted">
															<Icons.Annotation /> {comments}
														</Text>
													) : null}
												</Stack>
												{!hidePrice ? (
													<Box style={{ width: "60px" }} textAlign="right">
														<Money
															amount={given_price || 0}
															currency={currency}
															fontWeight="semibold"
															showCurrency
														/>
													</Box>
												) : null}
											</Inline>
										)
									)}
								</Stack>
							</Box>
						</Box>
					) : null}
				</Stack>
			</Stack>
			{!hidePrice &&
			(totalGivenPrice?.length ||
				totalTransportsPrice?.length ||
				totalTravelActivitiesPrice?.length ||
				totalTransportExtrasPrice?.length) ? (
				<Inline justifyContent="end" textAlign="right" marginTop="4">
					<Stack
						paddingX="4"
						paddingY="1"
						bgColor="subtle"
						borderWidth="1"
						rounded="md"
						fontWeight="semibold"
						gap="1"
					>
						{hasMultipleComponentPrices ? (
							<Box>
								Total: <MoneySum money={totalGivenPrice} />
							</Box>
						) : null}
						<Text fontSize={hasMultipleComponentPrices ? "sm" : undefined}>
							{joinAttributes(
								totalTransportsPrice?.length ? (
									<>
										{" "}
										Transports: <MoneySum money={totalTransportsPrice} />
									</>
								) : null,
								totalTravelActivitiesPrice?.length ? (
									<>
										Activity/Tickets:{" "}
										<MoneySum money={totalTravelActivitiesPrice} />
									</>
								) : null,
								totalTransportExtrasPrice?.length ? (
									<>
										Others: <MoneySum money={totalTransportExtrasPrice} />
									</>
								) : null
							)}
						</Text>
					</Stack>
				</Inline>
			) : null}
		</Box>
	) : null
}

function QuoteTravelActivityListItem({
	activity: quoteTravelActivity,
	hidePrice,
}: {
	activity: Omit<
		IQuote["travel_activities"][number],
		"ticket_tourist_configurations"
	> & {
		ticket_tourist_configurations: Array<
			IQuote["travel_activities"][number]["ticket_tourist_configurations"][number] & {
				fetching_prices?: boolean
			}
		>
	}
	hidePrice?: boolean
}) {
	const {
		activity,
		ticket_type,
		duration,
		slot,
		ticket_tourist_configurations,
		comments,
	} = quoteTravelActivity
	const totalGivenPrice = ticket_tourist_configurations.reduce<number>(
		(total, { given_price }) => total + Number(given_price || 0),
		0
	)
	const totalCalculatedPrice = ticket_tourist_configurations.reduce<
		number | undefined
	>(
		(total, { calculated_price }) =>
			calculated_price === undefined || calculated_price === null
				? undefined
				: Number(total || 0) + Number(calculated_price || 0),
		0
	)

	const isUsingSystemRates =
		Number(totalCalculatedPrice) &&
		Number(totalGivenPrice) === Number(totalCalculatedPrice)

	const currency = ticket_tourist_configurations[0].currency
	const fetching_prices = ticket_tourist_configurations.some((s) =>
		Boolean(s.fetching_prices)
	)

	return (
		<Inline gap="6" justifyContent="between">
			<Grid gap="4">
				<Col xs={12} sm={6}>
					<Stack gap="px">
						<Text fontWeight="semibold">
							{activity.name}{" "}
							{activity.deleted_at ? (
								<Badge warning>
									<Icons.Ban /> Disabled
								</Badge>
							) : null}
						</Text>
						{ticket_type ? (
							<Text fontSize="sm">
								<Inline gap="2" as="span">
									{ticket_type.name}
									<EntryTypeIcon
										entryType={ticket_type.entry_type}
										createdBy={ticket_type?.created_by?.name}
									/>
								</Inline>
								{duration
									? ` ${slot ? `- ${slot} hrs ` : ``}(${createDuration(
											duration
										).asMinutes()} mins)`
									: null}{" "}
								{ticket_type.deleted_at && !activity.deleted_at ? (
									<Badge warning>
										<Icons.Ban /> Disabled
									</Badge>
								) : null}
								{isTicketTypeClosedOnDate(
									{
										closed_on_date_intervals:
											ticket_type?.closed_on_date_intervals,
										closed_on_days_of_week: ticket_type?.closed_on_days_of_week,
									},
									localOrUtcTimestampToLocalDate(
										quoteTravelActivity.date_local,
										quoteTravelActivity.date
									)
								) ? (
									<Box>
										<Badge danger>
											<Icons.Ban /> Activity Closed
										</Badge>
									</Box>
								) : null}
							</Text>
						) : null}
					</Stack>
				</Col>
				<Col>
					<Inline gap="4" flexWrap="wrap">
						{ticket_tourist_configurations.map(
							(
								{
									configuration,
									quantity,
									calculated_price,
									per_quantity_given_price,
									given_price,
									currency,
									complementary,
								},
								index
							) => (
								<Stack key={`${activity.id}-${index}`}>
									<Text fontWeight="semibold">
										{quantity} {configuration.name}{" "}
										{complementary > 0 ? (
											<Box title="Free of Cost">({complementary} FOC)</Box>
										) : (
											""
										)}
									</Text>
									{hidePrice ? null : (
										<Box fontSize="sm">
											{per_quantity_given_price ? (
												<Text>
													<Money
														color={
															calculated_price &&
															Number(calculated_price) === Number(given_price)
																? "success"
																: "muted"
														}
														amount={per_quantity_given_price}
														currency={currency}
													/>{" "}
													* {quantity - complementary}
												</Text>
											) : (
												<Text color="warning">N/A</Text>
											)}
										</Box>
									)}
								</Stack>
							)
						)}
					</Inline>
					{comments ? (
						<Box paddingTop="1">
							<Text maxWidth="lg" color="muted" fontSize="sm">
								<Icons.Annotation /> {comments}
							</Text>
						</Box>
					) : null}
				</Col>
			</Grid>
			{!hidePrice ? (
				<Box style={{ width: "60px" }} textAlign="right">
					<Stack gap="1" alignItems="end">
						<Inline gap="1" alignItems="center" fontWeight="semibold">
							{isUsingSystemRates ? (
								<Icons.Ok color="success" title="Using System Rate" />
							) : null}
							<Money
								amount={totalGivenPrice}
								currency={currency}
								showCurrency
							/>
						</Inline>
						{isUsingSystemRates ? null : (
							<Text color="muted" fontSize="xs" title="System Rate">
								/{" "}
								{totalCalculatedPrice ? (
									<Money amount={totalCalculatedPrice} currency={currency} />
								) : (
									"N/A"
								)}
							</Text>
						)}
						{fetching_prices ? (
							<Text>
								<Badge warning>
									<Icons.Refresh spin size="3" /> Rates
								</Badge>
							</Text>
						) : null}
					</Stack>
				</Box>
			) : null}
		</Inline>
	)
}

export function QuoteExtras({
	extras,
	hidePrice,
	startDateUTC,
}: {
	extras: $PropertyType<IQuote, "other_extras">
	hidePrice?: boolean
	startDateUTC: string
}) {
	return extras.length ? (
		<Box as="section">
			<Box display="flex" overflow="auto">
				<Box display={{ xs: "none", sm: "block" }}>
					<SectionIcon>
						<Icons.StarEmpty size="6" />
					</SectionIcon>
				</Box>
				<Box width="full" flex="1" minWidth="0">
					<Box display="flex" marginBottom="2" alignItems="center">
						<Box display={{ xs: "block", sm: "none" }}>
							<SectionIcon>
								<Icons.StarEmpty size="6" />
							</SectionIcon>
						</Box>
						<Heading as="h5" fontSize="md">
							Other Special Inclusions
						</Heading>
					</Box>
					<Box display={{ xs: "block", sm: "none" }}>
						<Box as="ol" marginBottom="8">
							{extras.map(
								({ id, service, date, currency, given_price, comments }) => (
									<Box
										as="li"
										key={id}
										borderWidth="1"
										rounded="md"
										marginBottom="4"
										overflow="hidden"
										bgColor="default"
									>
										<Inline padding="4" gap="4">
											{date ? (
												<Time timestamp={date}>
													<Stack style={{ width: "36px" }}>
														<Text fontWeight="semibold">
															{withOrdinalSuffix(
																getDiff(
																	utcTimestampToLocalDate(date),
																	utcTimestampToLocalDate(startDateUTC),
																	"day"
																) + 1
															)}
														</Text>
														<Text color="muted" fontSize="sm">
															Day
														</Text>
													</Stack>
												</Time>
											) : null}
											<Stack gap="2">
												<Inline justifyContent="between" gap="4">
													<Text fontWeight="semibold">{service.name}</Text>
													{!hidePrice ? (
														<Box>
															<Money
																currency={currency}
																amount={given_price || 0}
																fontWeight="semibold"
																showCurrency
															/>
														</Box>
													) : null}
												</Inline>
												{comments ? (
													<Text maxWidth="lg" color="muted" fontSize="sm">
														<Icons.Annotation /> {comments}
													</Text>
												) : null}
											</Stack>
										</Inline>
									</Box>
								)
							)}
						</Box>
					</Box>
					<Box display={{ xs: "none", sm: "block" }}>
						<Table
							bordered
							responsive
							headers={["Day", "Service"].concat(hidePrice ? [] : ["Price"])}
							alignCols={{ 2: "right" }}
							rows={extras.map(
								({
									service,
									date,
									date_local,
									currency,
									given_price,
									comments,
								}) =>
									[
										date ? (
											<Stack>
												<Text fontWeight="semibold">
													{withOrdinalSuffix(
														getDiff(
															utcTimestampToLocalDate(date),
															utcTimestampToLocalDate(startDateUTC),
															"day"
														) + 1
													)}
												</Text>
												<Box>
													<Time
														timestamp={date}
														localTimestamp={date_local}
														format="ddd, D MMM"
														whiteSpace="preserve"
														fontSize="sm"
													/>
												</Box>
											</Stack>
										) : null,
										<Stack>
											<Box fontWeight="semibold">{service.name}</Box>
											{comments ? (
												<Text maxWidth="lg" color="muted" fontSize="sm">
													<Icons.Annotation /> {comments}
												</Text>
											) : null}
										</Stack>,
									].concat(
										hidePrice
											? []
											: [
													<Money
														amount={given_price || 0}
														currency={currency}
														fontWeight="semibold"
														showCurrency
													/>,
												]
									)
							)}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	) : null
}

export function QuoteVisas({
	visas,
	hidePrice,
	startDate,
}: {
	visas: $PropertyType<IQuote, "visas">
	hidePrice?: boolean
	startDate: Date
}) {
	const { xs, sm, md } = useBreakpoints()
	const totalGivenPrice = addMoneyFromDifferentCurrencies(
		visas.map((v) => v.given_price_money)
	)
	return visas.length ? (
		<Inline gap="4">
			<Box>
				<StepIcon
					color="primary"
					bgColor="primary"
					borderWidth="1"
					borderColor="primary_emphasis"
				>
					<Icons.GlobeWithBottomLine size="6" color="primary" />
				</StepIcon>
			</Box>
			<Stack width="full" gap="4">
				<Text fontSize={"md"} fontWeight="semibold">
					Visa
				</Text>
				{!sm && !xs && !md ? (
					<Table
						bordered
						responsive
						striped
						headers={[
							"Country",
							"Service & Service Type",
							"Visa Details",
							"Validity",
							"Pax",
							"Price",
						]}
						alignCols={{ 5: "right" }}
						rows={visas?.map((service) => [
							<Text fontWeight="semibold" fontSize="base">
								{service.country?.name ?? "-"}
							</Text>,
							<Stack gap="1">
								<Text fontWeight="semibold" fontSize="base">
									{service.visa_service?.name}
								</Text>
								<Text fontSize="base">{service.visa_type?.name}</Text>
								<VisaTypeDisabled visaType={service.visa_type} />
							</Stack>,
							<Inline gap="1">
								{joinAttributes(
									<Text fontSize="base">
										{getEntryTypeDisplayName(service.visa_type?.entry_type)}
									</Text>,
									<Text fontSize="base">
										{getPurposeOfVisaDisplayName(service.visa_type?.purpose)}
									</Text>
								)}
							</Inline>,
							<Box minWidth="max">
								<Text>Duration {service.visa_type?.duration_formatted}</Text>
								<Text>
									Processing Time{" "}
									{visaProcessingTimeStr(
										service.visa_type?.processing_time_in_hours
									)}
								</Text>
								{service.visa_type?.apply_before_days ? (
									<Text>
										Apply Minimum {service.visa_type?.apply_before_days} Days
										Prior
									</Text>
								) : null}
							</Box>,
							<Text fontWeight="semibold" fontSize="base">
								{service.quantity}
							</Text>,

							<Stack>
								<Money
									money={service.given_price_money}
									showCurrency
									fontWeight="semibold"
									fontSize="base"
								/>
								{service.visa_type?.cost_price &&
								!isVisaRateExpired({
									rateValidityDate: service.visa_type.price_valid_till,
									startDate: formatDate(startDate, "YYYY-MM-DD HH:mm:ss"),
								}) ? (
									<Money
										money={multipleMoneyBy(
											service.visa_type?.cost_price,
											service.quantity
										)}
										showCurrency
										fontSize="sm"
									/>
								) : (
									<Text fontSize="xs" color="muted">
										/ N/A
									</Text>
								)}
							</Stack>,
						])}
					/>
				) : (
					<Box as="ol" marginBottom="4">
						{visas
							?.filter((service) => Boolean(service.country))
							?.map((service, index) => (
								<Box
									as="li"
									key={
										service.visa_service?.id
											? `${service.visa_service.id}-${index}`
											: index
									}
									marginBottom="4"
									rounded="md"
									overflow="hidden"
									bgColor="default"
									borderWidth="1"
								>
									<Stack padding="4" gap="3">
										{/* Country Header */}
										<Inline justifyContent="between" alignItems="center">
											<Text fontWeight="semibold" fontSize="base">
												{service.country?.name}
											</Text>
											<Text fontSize="sm" color="muted">
												Pax:{" "}
												<Text as="span" fontWeight="semibold" color="default">
													{service.quantity}
												</Text>
											</Text>
										</Inline>

										{/* Visa Service & Type */}
										<Stack gap="1">
											<Text fontWeight="semibold" fontSize="base">
												{service.visa_service?.name}
											</Text>
											<Text fontSize="sm">{service.visa_type?.name}</Text>
											<VisaTypeDisabled visaType={service.visa_type} />
										</Stack>

										{/* Visa Details (Entry type, Purpose) */}
										{(service.visa_type?.entry_type ||
											service.visa_type?.purpose) && (
											<Inline gap="1" fontSize="sm" color="muted">
												{joinAttributes(
													service.visa_type?.entry_type ? (
														<Text fontSize="sm">
															{getEntryTypeDisplayName(
																service.visa_type?.entry_type
															)}
														</Text>
													) : null,
													service.visa_type?.purpose ? (
														<Text fontSize="sm">
															{getPurposeOfVisaDisplayName(
																service.visa_type?.purpose
															)}
														</Text>
													) : null
												)}
											</Inline>
										)}

										{/* Validity / Duration / Processing Time */}
										{(service.visa_type?.duration_formatted ||
											service.visa_type?.processing_time_in_hours ||
											service.visa_type?.apply_before_days) && (
											<Stack gap="1" fontSize="sm" color="muted">
												{service.visa_type?.duration_formatted ? (
													<Text fontSize="sm">
														Duration {service.visa_type?.duration_formatted}
													</Text>
												) : null}
												{service.visa_type?.processing_time_in_hours ? (
													<Text fontSize="sm">
														Processing Time{" "}
														{visaProcessingTimeStr(
															service.visa_type?.processing_time_in_hours
														)}
													</Text>
												) : null}
												{service.visa_type?.apply_before_days ? (
													<Text fontSize="sm">
														Apply Minimum {service.visa_type?.apply_before_days}{" "}
														Days Prior
													</Text>
												) : null}
											</Stack>
										)}

										{/* Price Section */}
										{!hidePrice && (
											<Inline
												justifyContent="between"
												alignItems="center"
												paddingTop="2"
												borderTopWidth="1"
											>
												<Text fontSize="sm" color="muted">
													Price:
												</Text>
												<Inline gap="1" alignItems="baseline">
													<Money
														money={service.given_price_money}
														showCurrency
														fontWeight="semibold"
														fontSize="base"
													/>
													{service.visa_type?.cost_price &&
													!isVisaRateExpired({
														rateValidityDate:
															service.visa_type.price_valid_till,
														startDate: formatDate(
															startDate,
															"YYYY-MM-DD HH:mm:ss"
														),
													}) ? (
														<Text fontSize="xs" color="muted">
															{" / "}
															<Money
																money={multipleMoneyBy(
																	service.visa_type?.cost_price,
																	service.quantity
																)}
																showCurrency
																fontSize="xs"
															/>
														</Text>
													) : (
														<Text fontSize="xs" color="muted">
															{" / N/A"}
														</Text>
													)}
												</Inline>
											</Inline>
										)}
									</Stack>
								</Box>
							))}
					</Box>
				)}
				{hidePrice ? null : (
					<Box textAlign="right">
						<Box
							display="inlineBlock"
							paddingX="4"
							paddingY="1"
							bgColor="subtle"
							borderWidth="1"
							rounded="md"
							fontWeight="semibold"
						>
							Total: <MoneySum money={totalGivenPrice} />
						</Box>
					</Box>
				)}
			</Stack>
		</Inline>
	) : null
}

export function SectionIcon(props: React.ComponentProps<typeof Box>) {
	return (
		<Box
			display="flex"
			width="12"
			height="12"
			alignItems="center"
			justifyContent="center"
			bgColor="primary"
			color="primary"
			borderWidth="1"
			borderColor="primary"
			fontSize="xl"
			rounded="full"
			marginRight="2"
			{...props}
		/>
	)
}

export function LatestQuoteDiffFromBookingsForTrip({
	tripId,
	containerPadding,
}: {
	tripId: number | string
	containerPadding?: BoxOwnProps["padding"]
}) {
	const { diff } = useDiffBetweenLatestQuoteAndBookings(tripId)
	return diff && diff.changed ? (
		<Box padding={containerPadding}>
			<Alert
				status="warning"
				title="Services details (Hotels/Transports) from Latest Quote and Latest Bookings are not up-to-date with each other."
			>
				<Stack gap="2">
					<Text>
						Some services provided in the latest quote have been updated in the
						booking process or vice-versa. Please check the services in quote
						and bookings, and update the details as per the requirements.
					</Text>
					<Component initialState={false}>
						{({ state: open, setState }) => (
							<Box>
								<Button onClick={() => setState(true)} status="warning">
									View Changes
								</Button>
								<Dialog open={open} onClose={() => setState(false)} xl>
									<Dialog.Header closeButton>
										<Dialog.Title>
											Changes in Services during booking
										</Dialog.Title>
									</Dialog.Header>
									<Dialog.Body>
										<Stack gap="4">
											<Stack
												gap="1"
												paddingX="4"
												paddingY="2"
												bgColor="subtle"
												rounded="md"
												borderWidth="1"
											>
												<Text color="danger">
													<Icons.Cancel />: exists in quotation but removed
													during bookings.
												</Text>
												<Text color="success">
													<Icons.Plus />: changed/added during bookings.
												</Text>
											</Stack>
											{diff.hotels_changed || diff.hotel_extras_changed ? (
												<DiffInQuoteHotels
													hotels={diff.hotels}
													hotelExtras={diff.hotel_extras}
												/>
											) : null}
											{diff.cabs_changed ? (
												<DiffInQuoteCabs cabs={diff.cabs} />
											) : null}
											{diff.travel_activities_changed ? (
												<DiffInQuoteTravelActivities
													travelActivities={diff.travel_activities}
												/>
											) : null}
											{diff.visas_changed ? (
												<DiffInQuoteVisas visas={diff.visas} />
											) : null}
										</Stack>
									</Dialog.Body>
									<Dialog.Footer>
										<Stack gap="2">
											<Text>
												<b>Next:</b> Please update the bookings/quotation and
												match the details.
											</Text>
											<Box>
												<Button level="primary" onClick={() => setState(false)}>
													Ok, Got it.
												</Button>
											</Box>
										</Stack>
									</Dialog.Footer>
								</Dialog>
							</Box>
						)}
					</Component>
				</Stack>
			</Alert>
		</Box>
	) : null
}

function DiffInQuoteHotels({
	hotels,
	hotelExtras,
}: {
	hotels: $PropertyType<IQuoteDiff, "hotels">
	hotelExtras: $PropertyType<IQuoteDiff, "hotel_extras">
}) {
	return hotels.length || hotelExtras.length ? (
		<Box as="section">
			<Inline gap="4">
				<Box>
					<SectionIcon>
						<Icons.Bed size="6" />
					</SectionIcon>
				</Box>
				<Stack flex="1" minWidth="0" width="full" gap="4">
					{hotels.length ? (
						<Stack gap="2">
							<Heading as="h6" fontSize="md">
								Accommodation
							</Heading>
							<Table
								bordered
								responsive
								headers={["", "Date", "Hotel", "Meal Plan", "Rooms"]}
							>
								<tbody>
									{hotels.map((quoteHotel, i) => {
										const {
											added,
											removed,
											hotel,
											date,
											date_local,
											meal_plan,
											room_type,
											no_of_rooms,
											persons_per_room,
											comments,
											adults_with_extra_bed,
											children_with_extra_bed,
											children_without_extra_bed,
										} = quoteHotel
										return (
											<Box
												as="tr"
												key={i}
												title={
													added ? "Added" : removed ? "Changed" : "Unchanged"
												}
												bgColor={
													added ? "success" : removed ? "danger" : undefined
												}
											>
												<TableDataCell
													textAlign="center"
													verticalAlign="middle"
													style={{
														width: "50px",
													}}
													bgColor={
														added
															? "success_emphasis"
															: removed
																? "danger_emphasis"
																: undefined
													}
													color={
														added
															? "on_emphasis"
															: removed
																? "on_emphasis"
																: undefined
													}
												>
													{added ? (
														<Icons.Plus title="Added" />
													) : removed ? (
														<Icons.Cancel title="Changed" />
													) : null}
												</TableDataCell>
												<TableDataCell>
													<Time
														whiteSpace="preserve"
														timestamp={date}
														localTimestamp={date_local}
													/>
												</TableDataCell>
												<TableDataCell>
													<Box>
														<Box display="inline" fontWeight="semibold">
															{hotel.name}
														</Box>{" "}
														<Box fontSize="sm" display="inlineBlock">
															{hotel.location.short_name}
															{hotel.stars_string
																? `, ${hotel.stars_string}`
																: null}
														</Box>
														{hotel.deleted_at ? (
															<Badge warning>Disabled</Badge>
														) : null}
														{comments ? (
															<blockquote>{comments}</blockquote>
														) : null}
													</Box>
												</TableDataCell>
												<TableDataCell>{meal_plan.name}</TableDataCell>
												<TableDataCell>
													<Box>
														<Box display="inline" fontWeight="semibold">
															{no_of_rooms} {room_type.name}
														</Box>{" "}
														<Box display="inlineBlock" fontSize="sm">
															(
															{joinAttributes(
																`${persons_per_room}P`,
																[
																	adults_with_extra_bed,
																	`${adults_with_extra_bed} AwEb`,
																],
																[
																	children_with_extra_bed,
																	`${children_with_extra_bed} CwEb`,
																],
																[
																	children_without_extra_bed,
																	`${children_without_extra_bed} CNB`,
																]
															)}
															)
														</Box>
													</Box>
												</TableDataCell>
											</Box>
										)
									})}
								</tbody>
							</Table>
						</Stack>
					) : null}
					{hotelExtras.length ? (
						<Stack gap="2">
							<Heading as="h6" fontSize="md">
								Hotel Special Inclusions
							</Heading>
							<Table
								bordered
								responsive
								headers={["", "Service", "Date", "Hotel"]}
							>
								<tbody>
									{hotelExtras.map(
										(
											{
												service,
												date,
												date_local,
												hotel,
												comments,
												added,
												removed,
											},
											i
										) => (
											<Box
												as="tr"
												key={i}
												title={
													added ? "Added" : removed ? "Changed" : "Unchanged"
												}
												backgroundColor={
													added ? "success" : removed ? "danger" : undefined
												}
											>
												<TableDataCell
													textAlign="center"
													verticalAlign="middle"
													style={{
														width: "50px",
													}}
													bgColor={
														added ? "success" : removed ? "danger" : undefined
													}
													color={
														added ? "success" : removed ? "danger" : undefined
													}
												>
													{added ? (
														<Icons.Plus title="Added" />
													) : removed ? (
														<Icons.Cancel title="Changed" />
													) : null}
												</TableDataCell>
												<TableDataCell>
													<Box>
														<Box fontWeight="semibold">{service.name}</Box>
														{comments ? (
															<blockquote>{comments}</blockquote>
														) : null}
													</Box>
												</TableDataCell>
												<TableDataCell>
													{date ? (
														<Time
															whiteSpace="preserve"
															timestamp={date}
															localTimestamp={date_local}
														/>
													) : null}
												</TableDataCell>
												<TableDataCell>
													{hotel && (
														<Box>
															<Box fontWeight="semibold" display="inline">
																{hotel.name}
															</Box>{" "}
															<Box fontSize="sm" display="inline">
																{hotel.location.short_name}
																{hotel.stars_string
																	? `, ${hotel.stars_string}`
																	: null}
															</Box>
														</Box>
													)}
												</TableDataCell>
											</Box>
										)
									)}
								</tbody>
							</Table>
						</Stack>
					) : null}
				</Stack>
			</Inline>
		</Box>
	) : null
}

function DiffInQuoteCabs({
	cabs,
}: {
	cabs: $PropertyType<IQuoteDiff, "cabs">
}) {
	return cabs.length ? (
		<Box as="section">
			<Inline gap="4">
				<Box>
					<SectionIcon>
						<Icons.Taxi size="6" />
					</SectionIcon>
				</Box>
				<Stack flex="1" minWidth="0" width="full" gap="4">
					{cabs.length ? (
						<Stack gap="2">
							<Heading as="h6" fontSize="md">
								Transportation
							</Heading>
							<Table
								bordered
								responsive
								headers={["", "Date", "Transport Service", "Cabs"]}
							>
								<tbody>
									{cabs.map((quoteCab, i) => {
										const {
											added,
											removed,
											transport_service,
											date,
											date_local,
											cab_type,
											no_of_cabs,
										} = quoteCab
										return (
											<Box
												as="tr"
												key={i}
												title={
													added ? "Added" : removed ? "Changed" : "Unchanged"
												}
												bgColor={
													added ? "success" : removed ? "danger" : undefined
												}
											>
												<TableDataCell
													textAlign="center"
													verticalAlign="middle"
													style={{
														width: "50px",
													}}
													bgColor={
														added
															? "success_emphasis"
															: removed
																? "danger_emphasis"
																: undefined
													}
													color={
														added
															? "on_emphasis"
															: removed
																? "on_emphasis"
																: undefined
													}
												>
													{added ? (
														<Icons.Plus title="Added" />
													) : removed ? (
														<Icons.Cancel title="Changed" />
													) : null}
												</TableDataCell>
												<TableDataCell>
													<Time
														whiteSpace="preserve"
														timestamp={date}
														localTimestamp={date_local}
													/>
												</TableDataCell>
												<TableDataCell>
													<Box fontWeight="semibold">
														{transport_service.name}
													</Box>
												</TableDataCell>
												<TableDataCell>
													{no_of_cabs}-{cab_type.name}
												</TableDataCell>
											</Box>
										)
									})}
								</tbody>
							</Table>
						</Stack>
					) : null}
				</Stack>
			</Inline>
		</Box>
	) : null
}

function DiffInQuoteTravelActivities({
	travelActivities,
}: {
	travelActivities: $PropertyType<IQuoteDiff, "travel_activities">
}) {
	return travelActivities.length ? (
		<Box as="section">
			<Inline gap="4">
				<Box>
					<SectionIcon>
						<Icons.Ticket size="6" />
					</SectionIcon>
				</Box>
				<Stack flex="1" minWidth="0" width="full" gap="4">
					{travelActivities.length ? (
						<Stack gap="2">
							<Heading as="h6" fontSize="md">
								Travel Activities / Tickets
							</Heading>
							<Table
								bordered
								responsive
								headers={["", "Date", "Service", "Configruation"]}
							>
								<tbody>
									{travelActivities.map((quoteCab, i) => {
										const {
											added,
											removed,
											activity,
											date,
											date_local,
											ticket_type,
											ticket_tourist_configurations,
										} = quoteCab
										return (
											<Box
												as="tr"
												key={i}
												title={
													added ? "Added" : removed ? "Changed" : "Unchanged"
												}
												bgColor={
													added ? "success" : removed ? "danger" : undefined
												}
											>
												<TableDataCell
													textAlign="center"
													verticalAlign="middle"
													style={{
														width: "50px",
													}}
													bgColor={
														added
															? "success_emphasis"
															: removed
																? "danger_emphasis"
																: undefined
													}
													color={
														added
															? "on_emphasis"
															: removed
																? "on_emphasis"
																: undefined
													}
												>
													{added ? (
														<Icons.Plus title="Added" />
													) : removed ? (
														<Icons.Cancel title="Changed" />
													) : null}
												</TableDataCell>
												<TableDataCell>
													<Time
														whiteSpace="preserve"
														timestamp={date}
														localTimestamp={date_local}
													/>
												</TableDataCell>
												<TableDataCell>
													<Box fontWeight="semibold">
														{activity.name}
														{ticket_type ? `-${ticket_type.name}` : ``}
													</Box>
												</TableDataCell>
												<TableDataCell>
													{ticket_tourist_configurations
														? joinAttributes(
																...ticket_tourist_configurations.map(
																	(config) =>
																		`${config.quantity} ${config.configuration.name}`
																)
															)
														: null}
												</TableDataCell>
											</Box>
										)
									})}
								</tbody>
							</Table>
						</Stack>
					) : null}
				</Stack>
			</Inline>
		</Box>
	) : null
}

function DiffInQuoteVisas({
	visas,
}: {
	visas: $PropertyType<IQuoteDiff, "visas">
}) {
	return visas.length ? (
		<Box as="section">
			<Inline gap="4">
				<Box>
					<SectionIcon>
						<Icons.GlobeWithBottomLine size="6" color="primary" />
					</SectionIcon>
				</Box>
				<Stack flex="1" minWidth="0" width="full" gap="4">
					{visas.length ? (
						<Stack gap="2">
							<Heading as="h6" fontSize="md">
								Visa
							</Heading>
							<Table
								bordered
								responsive
								headers={[
									"",
									"Country",
									"Service & Service Type",
									"Visa Details",
									"Validity",
									"Pax",
								]}
							>
								<tbody>
									{visas.map((quoteVisa, i) => {
										const {
											added,
											removed,
											country,
											visa_service,
											visa_type,
											quantity,
											comments,
										} = quoteVisa
										const entryType = getEntryTypeDisplayName(
											visa_type?.entry_type
										)
										const purpose = getPurposeOfVisaDisplayName(
											visa_type?.purpose
										)
										return (
											<Box
												as="tr"
												key={i}
												title={
													added ? "Added" : removed ? "Changed" : "Unchanged"
												}
												bgColor={
													added ? "success" : removed ? "danger" : undefined
												}
											>
												<TableDataCell
													textAlign="center"
													verticalAlign="middle"
													style={{
														width: "50px",
													}}
													bgColor={
														added
															? "success_emphasis"
															: removed
																? "danger_emphasis"
																: undefined
													}
													color={
														added
															? "on_emphasis"
															: removed
																? "on_emphasis"
																: undefined
													}
												>
													{added ? (
														<Icons.Plus title="Added" />
													) : removed ? (
														<Icons.Cancel title="Changed" />
													) : null}
												</TableDataCell>
												<TableDataCell>
													<Text fontWeight="semibold">
														{country?.name ?? "-"}
													</Text>
												</TableDataCell>
												<TableDataCell>
													<Box>
														<Box fontWeight="semibold">
															{visa_service?.name}
														</Box>
														{visa_type?.name ? (
															<Box fontSize="sm">{visa_type.name}</Box>
														) : null}
														<VisaTypeDisabled visaType={visa_type} />
														{comments ? (
															<blockquote>{comments}</blockquote>
														) : null}
													</Box>
												</TableDataCell>
												<TableDataCell>
													<Inline gap="1">
														{joinAttributes(
															[Boolean(entryType), entryType],
															[Boolean(purpose), purpose]
														)}
													</Inline>
												</TableDataCell>
												<TableDataCell>
													<Stack>
														{visa_type?.duration_formatted ? (
															<Text>
																Duration {visa_type.duration_formatted}
															</Text>
														) : null}
														{visa_type?.processing_time_in_hours ? (
															<Text>
																Processing Time{" "}
																{visaProcessingTimeStr(
																	visa_type.processing_time_in_hours
																)}
															</Text>
														) : null}
														{visa_type?.apply_before_days ? (
															<Text>
																Apply Minimum {visa_type.apply_before_days} Days
																Prior
															</Text>
														) : null}
													</Stack>
												</TableDataCell>
												<TableDataCell>
													<Text fontWeight="semibold">{quantity}</Text>
												</TableDataCell>
											</Box>
										)
									})}
								</tbody>
							</Table>
						</Stack>
					) : null}
				</Stack>
			</Inline>
		</Box>
	) : null
}

export function QuoteFlights({
	flightsDetails,
	flightPrice,
	hidePrice,
}: {
	flightsDetails: Array<{
		groups: Array<IPreviewFlightData>
		pricing: IFlightPricing
	}> //groups of flight and a group of flight can have multiple schedules
	hidePrice?: boolean
	flightPrice?: IFlightPricing[]
	startDateUTC?: string
}) {
	// atlest one group should have one schedule flight
	return flightsDetails.length ? (
		<Box as="section">
			<Box display="flex" overflow="auto">
				<Box display={{ xs: "none", sm: "block" }}>
					<SectionIcon>
						<Icons.Airplane size="6" />
					</SectionIcon>
				</Box>
				<Box flex="1" width="full" minWidth="0">
					<Box>
						<Box display="flex" marginBottom="2" alignItems="center">
							<Box display={{ xs: "block", sm: "none" }} marginRight="2">
								<SectionIcon>
									<Icons.Airplane size="6" />
								</SectionIcon>
							</Box>
							<Heading as="h5" fontSize="md">
								Flights
							</Heading>
						</Box>
						<Stack gap="6">
							{flightsDetails.map(({ groups, pricing }, index) => (
								<Grid
									justifyContent="between"
									alignItems="end"
									gap="4"
									key={index}
								>
									<Col>
										<Box
											borderWidth="1"
											rounded="md"
											overflow="hidden"
											bgColor="default"
										>
											<Stack
												padding={"4"}
												justifyContent={"center"}
												alignItems={"center"}
												width={"full"}
											>
												{groups?.map(
													({ type_of_flight, flight_details }, index) => (
														<Stack
															key={index}
															alignItems={"center"}
															maxWidth={"4xl"}
															width={"full"}
															paddingTop={"2"}
														>
															<Stack
																position={"relative"}
																paddingX={"2"}
																paddingY={"4"}
																borderRadius="md"
																borderWidth={"1"}
																borderColor={"muted"}
																width={"full"}
															>
																{type_of_flight !== "Multi City" ||
																(type_of_flight === "Multi City" &&
																	index < 1) ? (
																	<Inline
																		position={"absolute"}
																		borderRadius="lg"
																		borderWidth={"1"}
																		borderColor={"muted"}
																		backgroundColor={"primary"}
																		paddingX={"1"}
																		style={{
																			top: "-15px",
																		}}
																		alignItems={"center"}
																		gap={"1"}
																	>
																		{type_of_flight === "Return" ? (
																			<Icons.CircleArrow />
																		) : (
																			<Icons.CircleArrow rotate="180" />
																		)}
																		{type_of_flight}
																	</Inline>
																) : null}
																{flight_details.map(
																	(
																		{
																			departure,
																			arrival,
																			duration_formatted,
																			flight,
																		},
																		index
																	) => (
																		<Box key={index} width={"full"}>
																			<Grid width={"full"} gap="0">
																				<Col xs={4} sm={4} md={4} lg={4}>
																					<Box>
																						<Inline
																							alignItems={"center"}
																							gap={"2"}
																							justifyContent={"start"}
																							flexWrap={"wrap"}
																						>
																							<Text fontWeight={"semibold"}>
																								{departure?.airport?.city
																									?.name ||
																									departure?.airport?.iata ||
																									departure?.airport?.name ||
																									""}
																							</Text>
																							{departure?.schedule_date_local ? (
																								<Time
																									value={
																										departure.schedule_date_local
																									}
																									timeFormat="HH:mm"
																									fontWeight={"normal"}
																								/>
																							) : null}
																						</Inline>
																						{departure?.schedule_date_local ? (
																							<Time
																								value={
																									departure.schedule_date_local
																								}
																								timeFormat="ddd, DD MMMM YYYY"
																								fontWeight={"normal"}
																								fontSize={"sm"}
																							/>
																						) : null}
																						{departure?.airport?.timezone
																							?.name ? (
																							<Inline gap={"1"} color={"muted"}>
																								<Icons.Global />
																								<Text
																									fontWeight={"normal"}
																									fontSize={"sm"}
																								>
																									{getTimezoneOffset(
																										departure.schedule_date_local,
																										departure?.airport?.timezone
																											?.name
																									)}
																								</Text>
																							</Inline>
																						) : null}
																					</Box>
																				</Col>
																				<Col xs={4} sm={4} md={4} lg={4}>
																					<Box position={"relative"}>
																						<Stack
																							alignItems={"center"}
																							zIndex="10"
																							justifyContent={"center"}
																							width={"full"}
																						>
																							<Text
																								fontWeight={"semibold"}
																								fontSize={"sm"}
																								textAlign={"center"}
																							>
																								{flight.airline?.name}{" "}
																								{flight.number_plate}
																							</Text>
																							<Box
																								borderWidth={"1"}
																								borderColor={"black"}
																								style={{
																									borderStyle: "dashed",
																								}}
																								width={"full"}
																								maxWidth={"md"}
																							/>
																							<Text
																								fontWeight={"semibold"}
																								fontSize={"sm"}
																							>
																								{duration_formatted}
																							</Text>
																						</Stack>
																					</Box>
																				</Col>
																				<Col xs={4} sm={4} md={4} lg={4}>
																					<Box textAlign={"right"}>
																						<Inline
																							alignItems={"center"}
																							gap={"2"}
																							fontWeight={"semibold"}
																							justifyContent={"end"}
																							flexWrap={"wrap"}
																						>
																							<Text>
																								{arrival?.airport?.city?.name ||
																									arrival?.airport?.iata ||
																									arrival?.airport?.name ||
																									""}
																							</Text>
																							{arrival?.schedule_date_local ? (
																								<Time
																									value={
																										arrival.schedule_date_local
																									}
																									timeFormat="HH:mm"
																									fontWeight={"normal"}
																								/>
																							) : null}
																						</Inline>
																						{arrival?.schedule_date_local ? (
																							<Time
																								value={
																									arrival.schedule_date_local
																								}
																								timeFormat="ddd, DD MMMM YYYY"
																								fontWeight={"normal"}
																								fontSize={"sm"}
																							/>
																						) : null}
																						{arrival?.airport?.timezone
																							?.name ? (
																							<Inline
																								gap={"1"}
																								justifyContent={"end"}
																								color={"muted"}
																							>
																								<Icons.Global />
																								<Text
																									fontWeight={"normal"}
																									fontSize={"sm"}
																								>
																									{getTimezoneOffset(
																										arrival.schedule_date_local,
																										arrival?.airport?.timezone
																											?.name
																									)}
																								</Text>
																							</Inline>
																						) : null}
																					</Box>
																				</Col>
																			</Grid>

																			{index < flight_details.length - 1 ? (
																				<Divider sm />
																			) : null}
																		</Box>
																	)
																)}
															</Stack>

															{index < groups.length - 1 &&
															type_of_flight !== "One Way" ? (
																<Box
																	display={"flex"}
																	alignItems={"center"}
																	justifyContent={"center"}
																	rounded="full"
																	size="8"
																	borderWidth={"1"}
																	borderColor={"muted"}
																	backgroundColor={"primary"}
																	marginY={"4"}
																>
																	{type_of_flight === "Return" ||
																	type_of_flight === "Onward" ? (
																		<Icons.SwitchHorizontal rotate="90" />
																	) : (
																		<Icons.ArrowLeft rotate="270" />
																	)}
																</Box>
															) : null}
														</Stack>
													)
												)}
											</Stack>
										</Box>
									</Col>
									<Col xs={12} sm={12} md={12} lg={4}>
										<Box>
											<Inline
												gap="4"
												alignItems={"end"}
												justifyContent="end"
												flexWrap={"wrap"}
											>
												<Box>
													<Inline
														padding="4"
														bgColor="subtle"
														borderWidth="1"
														rounded="md"
														fontWeight="semibold"
														gap="1"
														whiteSpace="nowrap"
														justifyContent={"end"}
													>
														Pax :{" "}
														{`${pricing.no_of_adults}A ${pricing.children ? `, ${pricing.children}C` : ""} ${pricing.infants ? `, ${pricing.infants}Inf` : ""}`}
													</Inline>
												</Box>
												{!hidePrice && flightPrice ? (
													<Stack
														padding="4"
														bgColor="subtle"
														borderWidth="1"
														rounded="md"
														gap={"1"}
														maxWidth={"sm"}
													>
														<Inline
															gap="1"
															whiteSpace="nowrap"
															alignItems="end"
															justifyContent={"end"}
														>
															<Text fontWeight="semibold">Total Price: </Text>
															<Box whiteSpace="preserve" fontWeight="semibold">
																<Money
																	amount={pricing.given_price || 0}
																	currency={pricing.currency}
																	fontWeight="semibold"
																	showCurrency
																/>

																{pricing.calculated_price ? (
																	<>
																		<Box
																			title="Calculate Price"
																			as="span"
																			fontSize="xs"
																			color="muted"
																		>
																			{" "}
																			/{" "}
																			<Money
																				amount={pricing.calculated_price || 0}
																				currency={pricing.currency}
																				fontWeight="semibold"
																				showCurrency
																			/>
																		</Box>
																	</>
																) : null}
															</Box>
														</Inline>
														{pricing.comments ? (
															<Box
																maxWidth="lg"
																color="muted"
																fontSize="sm"
																title="Comment"
															>
																<Icons.Annotation opacity="50" size="4" />{" "}
																{pricing.comments}
															</Box>
														) : null}
													</Stack>
												) : null}
											</Inline>
										</Box>
									</Col>
								</Grid>
							))}
						</Stack>

						<Inline
							paddingY={"2"}
							justifyContent={"between"}
							alignItems={"center"}
						>
							<PreviewFlightOnDialog flights={flightsDetails}>
								{({ open }) => <Button onClick={open}>View More</Button>}
							</PreviewFlightOnDialog>
						</Inline>
					</Box>
				</Box>
			</Box>
		</Box>
	) : null
}
